/**
 * 视频 Agent - 聊天式布局
 * 顶部标题，中间视频卡片流（按日期分组），底部输入区
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ModelArkReferenceLibraryPanel } from '../assets/ModelArkLibraryControls';
import { VIDEO_PROVIDER_VOLCENGINE_SEEDANCE, DOMESTIC_SEEDANCE_MODELS, DOMESTIC_SEEDANCE_DURATIONS, DOMESTIC_SEEDANCE_RATIOS, getDomesticSeedanceResolutions, normalizeDomesticSeedanceResolution, isDomesticOfficialSeedance } from '../../utils/domesticSeedanceCapabilities';
import {
  Film, Play, Pause, Image as ImageIcon, Upload,
  Loader2, AlertCircle, Video, X, Plus,
  Download, ChevronDown,
  Maximize2, Trash2,
  FolderOpen, RefreshCw, Send,
  Check, Sparkles, Globe, Smile, Folder, Drama, Mountain, Wrench, Clapperboard, Palette,
  Music, FileText, Link2
} from 'lucide-react';
import { LIGHTING_PRESETS, applyLighting } from '../storyboard/common/LightingPresets';
import { useTheme } from '../../context/ThemeContext';
import { useUser } from '../../context/UserContext';
import { useToast } from '../../context/ToastContext';
import EstimatedPoints from '../common/EstimatedPoints';
import { authenticatedFetch } from '../../utils/api';
import { API_HOST } from '../../utils/constants';
import { recycleStoredMedia } from '../../services/resourceRecycleService';
import {
  submitVideoTask,
  submitWandVideoAgentTask,
  startPolling,
  cancelTask,
  generateOrbitVideo,
  generateSubjectReference,
  submitEraseSubtitle,
  startPostProcessPolling,
} from '../../services/videoService';
import ReactDOM, { flushSync } from 'react-dom';
import '../ui/GenerationSurface.css';
import ResourceSelector from '../resource/ResourceSelector';
import { uploadFileToCos, uploadFileMultipart } from '../../utils/fileUtils';
import { ConfirmDialog } from '@ui';
import PostProcessInput from './PostProcessInput';
import PostProcessCard from './PostProcessCard';
import {
  getActiveWanxDraftMedia,
  restoreVideoReferenceDraft,
  resolveVideoDraftMode,
  switchVideoReferenceBank,
} from './videoReferenceDraft';
import { useAssetStatusMap } from '../../hooks/useAssetStatusMap';
import { useDelayedUnmount } from '../../hooks/useDelayedUnmount';
import { serializeEditor, stripRefMarkers, resolveGuaVideoRefMarkers, resolveRefMarkersToCharacter, createMentionChip, renderPromptToEditor } from '../../utils/mentionUtils';
import { applyGuaVoiceBindingPrompt } from '../../utils/guaVoiceBindings';
import { getEffectiveAssetMeta } from '../../utils/guaVideoAssetStatus';
import { preflightVideoAssetContents } from '../../utils/videoAssetPreflight';
import { getDreaminaProxyOriginalUrl, isDreaminaProxySeedance20Provider, requireDreaminaProxyOriginalUrl } from '../../utils/dreaminaProxyReferences';
import {
  getGuaOminiVideoResolutions,
  getProjectDefaultVideoRatio,
  getProjectVideoSubmissionProvider,
  isOverseasVideoProvider,
  isSeedanceVideoProvider,
  isWan3Project,
  normalizeVideoResolutionForProvider,
  VIDEO_PROVIDER_WANX,
} from '../../utils/projectRuntime';
import {
  WANX_VIDEO_ACCEPT,
  WANX_VIDEO_DURATION_OPTIONS,
  WANX_VIDEO_MEDIA_TYPES,
  WANX_VIDEO_MODEL,
  WANX_VIDEO_MODELS,
  WANX_VIDEO_RATIOS,
  WANX_VIDEO_RESOLUTIONS,
  assertWanxVideoMediaSelection,
  createWanxVideoMediaItem,
  getWanxVideoMediaLabel,
  inferWanxVideoReferenceType,
  isWanxVideoProvider,
  normalizeWanxVideoDuration,
  normalizeWanxVideoModel,
  normalizeWanxVideoResolution,
  resolveWanxVideoMediaMarkers,
  validateWanxVideoMediaFile,
  validateWanxVideoRequest,
} from '../../utils/wanxVideoCapabilities';
import {
  SEEDANCE25_MODEL,
  assertSeedance25SubmissionProvider,
  VIDEO_PROVIDER_AKOOL_SD25,
  VIDEO_PROVIDER_ASTROFLOW_SD25,
  VIDEO_PROVIDER_BYTEPLUS_SD25,
  VIDEO_PROVIDER_VOLCENGINE_SD25,
  VIDEO_PROVIDER_NEWAPI_SD25,
  buildSeedance25ReferencePayload,
  getSeedance25ReferenceMediaType,
  getSeedance25ReferenceUrl,
  supportsSeedance25MediaReferences,
  normalizeSeedance25ReferencePrompt,
  getSeedance25Capabilities,
  getSeedance25DurationOptions,
  getSeedance25RatiosForMode,
  normalizeSeedance25Duration,
  normalizeSeedance25Ratio,
  normalizeSeedance25RatioForMode,
  normalizeSeedance25Resolution,
  normalizeSeedance25SubmissionProvider,
  normalizeSeedance25ReferenceUrls,
  resolveSeedance25Provider,
  validateSeedance25References,
} from '../../utils/seedance25Capabilities';

// ── 打光预设从共享常量导入 ──
// import { LIGHTING_PRESETS, applyLighting } from '../storyboard/common/LightingPresets';

// ── 模型配置 ──
const buildSeedance25ModelConfig = (provider, color) => {
  const capabilities = getSeedance25Capabilities(provider);
  return {
    id: provider,
    name: capabilities.label,
    icon: Sparkles,
    color,
    modes: [...capabilities.modes],
    defaults: {
      model: capabilities.model,
      duration: `${capabilities.defaultDuration}s`,
      aspectRatio: '9:16',
      ratio: '9:16',
      resolution: capabilities.resolutions[0],
      generateAudio: true,
      ...(capabilities.supportsWatermarkControl ? { watermark: false } : {}),
    },
    models: [
      {
        value: capabilities.model,
        label: capabilities.label,
        desc: `最多 ${capabilities.maxReferenceImages} 张参考图，最长 ${capabilities.maxDuration} 秒${capabilities.referenceNotice ? `；${capabilities.referenceNotice}` : ''}`,
      },
    ],
    durations: getSeedance25DurationOptions(provider),
    ratios: [...capabilities.ratios],
    resolutions: [...capabilities.resolutions],
    features: capabilities.supportsGenerateAudioControl ? ['generateAudio'] : [],
  };
};

const WAND_VEGA_PROVIDER = 'wand_vega';
const getWandVegaDurations = (version) => Array.from(
  { length: version === '1.5-pro' ? 27 : 12 },
  (_, index) => `${index + 4}s`,
);
const isPublicWandMediaUrl = (value) => {
  try {
    const url = new URL(String(value || ''));
    return ['http:', 'https:'].includes(url.protocol)
      && !['localhost', '127.0.0.1', '::1'].includes(url.hostname);
  } catch {
    return false;
  }
};
const getWandFrameTarget = (mode, frames) => (
  mode === 'img2video' || !frames.start ? 'start' : 'end'
);

const MODEL_CONFIGS = {
  [VIDEO_PROVIDER_VOLCENGINE_SEEDANCE]: {
    id: VIDEO_PROVIDER_VOLCENGINE_SEEDANCE, name: 'Seedance 2.0（国内官方）', icon: Sparkles, color: null,
    modes: ['ref2v', 'img2video', 'start-end', 'text2video'],
    defaults: { model: DOMESTIC_SEEDANCE_MODELS[0].id, duration: '5s', aspectRatio: '9:16', ratio: '9:16', resolution: '720p', generateAudio: true },
    models: DOMESTIC_SEEDANCE_MODELS.map((model) => ({ value: model.id, label: model.name, desc: '国内官方接口 · 共用国内官方素材库' })),
    durations: [...DOMESTIC_SEEDANCE_DURATIONS], ratios: [...DOMESTIC_SEEDANCE_RATIOS], resolutions: ['480p', '720p'], features: ['generateAudio'],
  },
  guavideo: {
    id: 'guavideo',
    name: 'GuaVideo',
    icon: Sparkles,
    color: null, // 运行时从 theme 取
    modes: ['ref2v', 'img2video', 'start-end', 'text2video', 'edit-video', 'extend-video'],
    defaults: {
      model: 'doubao-seedance-2-0-260128',
      duration: '5s',
      aspectRatio: '9:16',
      resolution: '480p',
      ratio: '9:16',
      generateAudio: true,
    },
    models: [
      { value: 'doubao-seedance-2-0-260128', label: '2.0 标准', desc: '最高品质' },
      { value: 'doubao-seedance-2-0-fast-260128', label: '2.0 Fast', desc: '更快速度' },
    ],
    durations: ['4s', '5s', '6s', '7s', '8s', '9s', '10s', '11s', '12s', '13s', '14s', '15s'],
    ratios: ['adaptive', '16:9', '4:3', '1:1', '3:4', '9:16', '21:9'],
    resolutions: ['480p', '720p'],
    features: ['generateAudio', 'webSearch']
  },
  engguavideo: {
    id: 'engguavideo',
    name: 'EngGuaVideo',
    icon: Globe,
    color: '#22c55e', // 模型品牌标识色，跨主题保留
    modes: ['ref2v', 'img2video', 'start-end', 'text2video', 'edit-video', 'extend-video'],
    defaults: {
      model: 'dreamina-seedance-2-0',
      duration: '5s',
      aspectRatio: '9:16',
      resolution: '480p',
      ratio: '9:16',
      generateAudio: true,
    },
    models: [
      { value: 'dreamina-seedance-2-0', label: 'Dreamina 2.0', desc: '新任务走 Dreamina v3 代理' },
      { value: 'dreamina-seedance-2-0-fast', label: 'Dreamina 2.0 Fast', desc: '新任务走 Dreamina v3 代理' },
    ],
    durations: ['4s', '5s', '6s', '7s', '8s', '9s', '10s', '11s', '12s', '13s', '14s', '15s'],
    ratios: ['adaptive', '16:9', '4:3', '1:1', '3:4', '9:16', '21:9'],
    resolutions: ['480p', '720p', '1080p'],
    features: ['generateAudio']
  },
  omini_seedance: {
    id: 'omini_seedance',
    name: 'GuaOmini',
    icon: Globe,
    color: '#38bdf8',
    modes: ['ref2v', 'img2video', 'start-end', 'text2video', 'edit-video', 'extend-video'],
    defaults: {
      model: 'dreamina-seedance-2-0',
      duration: '5s',
      aspectRatio: '9:16',
      resolution: '480p',
      ratio: '9:16',
      generateAudio: true,
    },
    models: [
      { value: 'dreamina-seedance-2-0', label: '2.0 标准', desc: '新任务走 Dreamina v3 代理' },
      { value: 'dreamina-seedance-2-0-fast', label: '2.0 Fast', desc: '新任务走 Dreamina v3 代理' },
    ],
    durations: ['4s', '5s', '6s', '7s', '8s', '9s', '10s', '11s', '12s', '13s', '14s', '15s'],
    ratios: ['adaptive', '16:9', '4:3', '1:1', '3:4', '9:16', '21:9'],
    resolutions: getGuaOminiVideoResolutions(),
    features: ['generateAudio']
  },
  [VIDEO_PROVIDER_AKOOL_SD25]: buildSeedance25ModelConfig(VIDEO_PROVIDER_AKOOL_SD25, '#0ea5e9'),
  [VIDEO_PROVIDER_NEWAPI_SD25]: buildSeedance25ModelConfig(VIDEO_PROVIDER_NEWAPI_SD25, '#f97316'),
  [VIDEO_PROVIDER_ASTROFLOW_SD25]: buildSeedance25ModelConfig(VIDEO_PROVIDER_ASTROFLOW_SD25, '#8b5cf6'),
  [VIDEO_PROVIDER_BYTEPLUS_SD25]: buildSeedance25ModelConfig(VIDEO_PROVIDER_BYTEPLUS_SD25, '#22c55e'),
  [VIDEO_PROVIDER_VOLCENGINE_SD25]: buildSeedance25ModelConfig(VIDEO_PROVIDER_VOLCENGINE_SD25, '#14b8a6'),
  wanx: {
    id: 'wanx',
    name: '万相 3.0',
    icon: Sparkles,
    color: '#6366f1',
    modes: ['text2video', 'img2video', 'start-end', 'reference'],
    defaults: {
      model: WANX_VIDEO_MODEL,
      duration: '5s',
      aspectRatio: 'adaptive',
      ratio: 'adaptive',
      resolution: '1080P',
      audio: true,
      watermark: false,
      promptExtend: true,
      seed: '',
    },
    models: [
      ...WANX_VIDEO_MODELS.map(({ value, label, description }) => ({
        value,
        label: `万相 3.0 ${label}`,
        desc: description,
      })),
    ],
    durations: WANX_VIDEO_DURATION_OPTIONS,
    ratios: WANX_VIDEO_RATIOS,
    resolutions: WANX_VIDEO_RESOLUTIONS,
    features: ['audio', 'seed', 'watermark'],
  },
  [WAND_VEGA_PROVIDER]: {
    id: WAND_VEGA_PROVIDER,
    name: 'Wand Vega Pro',
    icon: Sparkles,
    color: '#7c3aed',
    modes: ['text2video', 'img2video', 'start-end', 'reference'],
    defaults: {
      model: '1.0-pro',
      duration: '5s',
      aspectRatio: '9:16',
      ratio: '9:16',
      resolution: '720p',
      generateAudio: true,
    },
    models: [
      { value: '1.0-pro', label: '1.0 Pro', desc: '文档标注 4–15 秒' },
      { value: '1.5-pro', label: '1.5 Pro', desc: '文档标注 4–30 秒；具体素材和分辨率组合以腾讯服务端为准' },
    ],
    durations: getWandVegaDurations('1.0-pro'),
    ratios: ['adaptive', '21:9', '16:9', '4:3', '1:1', '3:4', '9:16'],
    resolutions: ['720p', '1080p', '2k', '4k'],
    features: ['generateAudio'],
  },
  vidu: {
    id: 'vidu',
    name: 'Vidu',
    icon: Smile,
    color: '#3b82f6',
    modes: ['text2video', 'img2video', 'start-end', 'reference'],
    defaults: {
      model: 'viduq3-turbo',
      duration: '5s',
      aspectRatio: '9:16',
      resolution: '720p',
      audio: true,
    },
    models: [
      { value: 'viduq3-turbo', label: 'Q3 Turbo', desc: '快速', durationRange: [1, 16], resolutions: ['540p', '720p', '1080p'], defaultResolution: '720p', supportsBgm: false, supportsAudio: true, supportsAudioType: false, supportsVoiceId: false, supportsMovementAmplitude: false },
      { value: 'viduq3-pro', label: 'Q3 Pro', desc: '高质量', durationRange: [1, 16], resolutions: ['540p', '720p', '1080p'], defaultResolution: '720p', supportsBgm: false, supportsAudio: true, supportsAudioType: false, supportsVoiceId: false, supportsMovementAmplitude: false },
      { value: 'vidu2.0', label: 'Vidu 2.0', desc: '最新', durationRange: [4, 8], resolutions: ['360p', '720p', '1080p'], defaultResolution: '360p', supportsBgm: false, supportsAudio: true, supportsAudioType: true, supportsVoiceId: true, supportsMovementAmplitude: true },
    ],
    durations: ['4s', '5s', '8s', '16s'],
    ratios: ['16:9', '9:16', '1:1'],
    features: ['audio', 'bgm', 'seed']
  },
  happyhorse: {
    id: 'happyhorse',
    name: 'HappyHorse',
    icon: Sparkles,
    color: '#10B981',
    modes: ['text2video', 'img2video', 'reference'],
    defaults: {
      model: 'happyhorse-t2v',
      duration: '5s',
      aspectRatio: '16:9',
      resolution: '1080P',
    },
    models: [
      { value: 'happyhorse-t2v', label: '文生视频', desc: '文本生成视频' },
      { value: 'happyhorse-i2v', label: '图生视频', desc: '首帧图生成视频' },
      { value: 'happyhorse-r2v', label: '参考生视频', desc: '多参考图生成视频' },
      { value: 'happyhorse-edit', label: '视频编辑', desc: '风格变换/局部替换' },
    ],
    durations: ['3s', '4s', '5s', '6s', '7s', '8s', '9s', '10s', '11s', '12s', '13s', '14s', '15s'],
    ratios: ['16:9', '9:16', '1:1', '4:3', '3:4'],
    resolutions: ['480P', '720P', '1080P'],
    features: []
  },
};

const isGuaLikeModel = isSeedanceVideoProvider;
const getVideoReferenceBank = (model) => isWanxVideoProvider(model) ? 'wanx'
  : model === WAND_VEGA_PROVIDER ? 'wand' : isGuaLikeModel(model) ? 'gua' : 'generic';
const MENTION_RENDER_LIMIT = 80;

const getRememberedVideoRatio = () => {
  try {
    return localStorage.getItem('video_ratio') || '9:16';
  } catch {
    return '9:16';
  }
};

const getPublicVideoModelName = (model) => {
  const value = String(model || '');
  const normalized = value.toLowerCase();
  if (normalized.includes('seedance-2-0-fast')) return '2.0 Fast';
  if (normalized.includes('seedance-2-0')) return '2.0 标准';
  return value.replace(/seedance/gi, '').replace(/--+/g, '-').trim();
};

// ── 下拉选择器小组件（避免 IIFE 里用 hook 导致 hook 数量不一致）──
const DropdownPicker = ({ label, value, options, color, onChange, ariaLabel }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menu = useDelayedUnmount(open, 150);
  return (
    <div className="relative" ref={btnRef}>
      <button onClick={() => setOpen(!open)}
        className="va-btn generation-dropdown flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium whitespace-nowrap"
        aria-label={ariaLabel || `${label || value}`}
        aria-expanded={open}
        style={{ backgroundColor: color ? `${color}20` : 'var(--color-bg-tertiary)', color: color || 'var(--color-text-secondary)' }}
      >{label || value} <ChevronDown size={9} className={open ? 'rotate-180' : ''} /></button>
      {menu.mounted && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[300]" onClick={() => setOpen(false)}>
          <div className="studio-generation generation-popover fixed rounded-lg py-2 px-1 min-w-[110px] max-h-52 overflow-y-auto custom-scrollbar"
            data-exiting={menu.exiting}
            style={{
              bottom: window.innerHeight - (btnRef.current?.getBoundingClientRect().top || 0) + 6,
              left: btnRef.current?.getBoundingClientRect().left || 0,
              backgroundColor: 'color-mix(in srgb, var(--color-bg-secondary) 88%, transparent)', backdropFilter: 'blur(24px) saturate(1.6)', WebkitBackdropFilter: 'blur(24px) saturate(1.6)', border: '1px solid var(--color-border-primary)',
            }}
            onClick={e => e.stopPropagation()}
          >
            {options.map(opt => {
              const optValue = typeof opt === 'string' ? opt : opt.value;
              const optLabel = typeof opt === 'string' ? opt : opt.label;
              const isSel = value === optValue;
              return (
                <button key={optValue} onClick={() => { onChange(optValue); setOpen(false); }}
                  className="va-drop-item generation-choice w-full px-3 py-2 rounded-lg text-xs text-left"
                  aria-pressed={isSel}
                  style={{ color: isSel ? (color || 'var(--color-accent-primary)') : 'var(--color-text-secondary)', backgroundColor: isSel ? (color ? `${color}18` : 'var(--color-accent-light)') : 'transparent' }}
                >{optLabel === 'adaptive' ? '自适应' : optLabel}</button>
              );
            })}
          </div>
        </div>, document.body
      )}
    </div>
  );
};

// ── @ 菜单大图预览（独立组件，state 变化不影响主组件） ──
const MentionPreview = React.memo(({ url, anchorRef }) => {
  if (!url) return null;
  const anchorRect = anchorRef?.current?.getBoundingClientRect();
  if (!anchorRect) return null;
  const previewWidth = Math.min(400, window.innerWidth * 0.3) + 20;
  const spaceRight = window.innerWidth - anchorRect.right;
  const pos = spaceRight >= previewWidth
    ? { left: anchorRect.right + 8, right: 'auto' }
    : { right: window.innerWidth - anchorRect.left + 8, left: 'auto' };
  return ReactDOM.createPortal(
    <div className="fixed z-[400] pointer-events-none" style={{ ...pos, bottom: window.innerHeight - anchorRect.bottom }}>
      <img src={url} alt="" className="rounded-xl shadow-2xl animate-in fade-in zoom-in-95 duration-150" style={{
        maxWidth: 'min(400px, 30vw)', maxHeight: 'min(400px, 40vh)', width: 'auto', height: 'auto', objectFit: 'contain',
        backgroundColor: 'color-mix(in srgb, var(--color-bg-overlay) 95%, transparent)', border: '1px solid var(--color-border-primary)', padding: '4px',
      }} />
    </div>,
    document.body
  );
});

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const isYesterday = d.toDateString() === new Date(now - 86400000).toDateString();
  if (isToday) return '今天';
  if (isYesterday) return '昨天';
  return `${d.getMonth() + 1}月${d.getDate()}日`;
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const getDateKey = (dateStr) => {
  if (!dateStr) return 'unknown';
  return new Date(dateStr).toDateString();
};

// ── 视频放大预览 Modal ──
const VideoLightbox = ({ video, onClose, exiting = false }) => {
  const videoRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${exiting ? 'animate-out fade-out duration-150' : 'animate-in fade-in duration-200'}`} style={{ backgroundColor: 'var(--overlay-heavy)' }} onClick={onClose}>
      <div className={`relative mx-4 ${exiting ? 'animate-out fade-out zoom-out-95 duration-150' : 'animate-in fade-in zoom-in-95 duration-200'}`} style={{ maxWidth: '90vw', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
        <video ref={videoRef} src={video.videoUrl} className="rounded-xl" style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', backgroundColor: '#000' }} controls autoPlay loop />
        <button onClick={onClose} className="absolute -top-3 -right-3 w-8 h-8 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: 'var(--overlay-medium)' }}>
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

// ── 视频卡片 ──
const VideoCard = React.memo(({ video, theme, onRetry, onDelete, onCancel, onEraseSubtitle }) => {
  const videoRef = useRef(null);
  const cardRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [progress, setProgress] = useState(0);
  const [erasing, setErasing] = useState(false);
  const [isVisible, setIsVisible] = useState(false); // 是否在可视区域
  const lightboxMount = useDelayedUnmount(lightbox, 150);
  const isPending = video.status === 'processing' || video.status === 'queued';
  const providerCapabilities = getSeedance25Capabilities(video.provider);
  // 没有取消接口的厂商处理中隐藏操作，避免“停止前端等待”被误解成服务商取消。
  const canCancel = isPending
    && video.provider !== WAND_VEGA_PROVIDER
    && (providerCapabilities ? providerCapabilities.supportsCancel : true);
  const canErase = video.status === 'success'
    && video.originalVideoUrl
    && video.provider === 'guavideo'
    && !erasing;
  const modelName = video.model || video.provider || '';

  // IntersectionObserver：只有可视区域内才挂载 <video>
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { rootMargin: '200px 0px' } // 提前 200px 开始加载
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play();
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const { currentTime, duration } = videoRef.current;
    if (duration > 0) setProgress((currentTime / duration) * 100);
  };

  // 离开可视区域时暂停视频
  useEffect(() => {
    if (!isVisible && isPlaying && videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [isVisible, isPlaying]);

  return (
    <>
      {lightboxMount.mounted && <VideoLightbox video={video} onClose={() => setLightbox(false)} exiting={lightboxMount.exiting} />}
      <div ref={cardRef} className="va-card generation-result group relative overflow-hidden"
        style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-secondary)' }}
      >
        {/* 视频/内容区 */}
        <div className="generation-result-media relative" style={{ backgroundColor: 'var(--color-bg-overlay)', maxHeight: 400, overflow: 'hidden', aspectRatio: '16/9' }}>
          {video.videoUrl ? (
            <>
              {isVisible ? (
                <video ref={videoRef} src={video.videoUrl} className="w-full h-full object-contain" style={{ maxHeight: 400 }}
                  onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)}
                  onTimeUpdate={handleTimeUpdate} onEnded={() => { setIsPlaying(false); setProgress(100); }}
                  poster={video.thumbnail || undefined}
                  preload="metadata"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-2" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'var(--color-bg-tertiary)' }}>
                    <Film size={20} style={{ color: 'var(--color-text-muted)', opacity: 0.4 }} />
                  </div>
                  {video.duration && (
                    <span className="text-2xs" style={{ color: 'var(--color-text-muted)', opacity: 0.4 }}>{video.duration}</span>
                  )}
                </div>
              )}
              {/* 播放控件 — 仅可见时渲染 */}
              {isVisible && (
                <>
                  {/* 播放按钮 */}
                  <button type="button" aria-label={isPlaying ? '暂停视频' : '播放视频'} className="generation-play-toggle generation-reveal absolute inset-0 flex items-center justify-center cursor-pointer" onClick={togglePlay}>
                    <span className="w-12 h-12 rounded-md flex items-center justify-center" style={{ backgroundColor: 'var(--overlay-medium)' }}>
                      {isPlaying ? <Pause size={22} className="text-white" /> : <Play size={22} className="text-white ml-0.5" />}
                    </span>
                  </button>
                  {/* 进度条 */}
                  <div className="generation-progress absolute bottom-0 left-0 right-0 h-0.5 cursor-pointer" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                    onClick={(e) => {
                      if (!videoRef.current) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const pct = (e.clientX - rect.left) / rect.width;
                      videoRef.current.currentTime = pct * videoRef.current.duration;
                    }}
                  >
                    <div className="h-full" style={{ width: `${progress}%`, backgroundColor: MODEL_CONFIGS[video.provider]?.color || 'var(--color-accent-primary)' }} />
                  </div>
                  {/* 放大 */}
                  <button className="generation-reveal generation-media-action absolute top-2.5 right-2.5 p-1.5 rounded-md" style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }} onClick={() => setLightbox(true)} aria-label="放大视频" title="放大">
                    <Maximize2 size={13} />
                  </button>
                </>
              )}
              {/* 时长 */}
              {video.duration && (
                <div className="absolute top-2.5 left-2.5 px-1.5 py-0.5 rounded text-2xs font-medium" style={{ backgroundColor: 'var(--overlay-medium)', color: 'rgba(255,255,255,0.8)' }}>
                  {video.duration}
                </div>
              )}
            </>
          ) : (
            <div className="w-full flex flex-col items-center justify-center gap-2" style={{ minHeight: 200 }}>
              {video.status === 'processing' || video.status === 'queued' ? (
                <>
                  <Loader2 size={28} className="animate-spin" style={{ color: MODEL_CONFIGS[video.provider]?.color || 'var(--color-text-muted)' }} />
                  <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{video.status === 'queued' ? '排队中...' : '生成中...'}</span>
                  {video.progress > 0 && <div className="w-28 h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}><div className="h-full rounded-full generation-feedback" style={{ width: `${video.progress}%`, backgroundColor: MODEL_CONFIGS[video.provider]?.color || 'var(--color-text-muted)' }} /></div>}
                </>
              ) : video.status === 'failed' ? (
                <>
                  <AlertCircle size={24} style={{ color: 'var(--color-error)' }} />
                  <span className="text-xs" style={{ color: 'var(--color-error)' }}>{video.error || '生成失败'}</span>
                  <button
                    type="button"
                    onClick={() => onRetry(video)}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs transition-colors hover:bg-[var(--color-bg-tertiary)]"
                    style={{ color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
                  >
                    <RefreshCw size={12} />
                    回填参数
                  </button>
                </>
              ) : (
                <Film size={28} style={{ color: 'var(--color-text-muted)', opacity: 0.3 }} />
              )}
            </div>
          )}
        </div>

        {/* 底部信息 */}
        <div className="generation-result-body px-3 py-2.5">
          <p className="text-2xs leading-relaxed line-clamp-2 mb-2" style={{ color: 'var(--color-text-muted)' }}>
            {video.prompt || '无提示词'}
          </p>
          <div className="flex items-center justify-between">
            <div className="generation-result-meta flex items-center gap-2">
              <span className="text-2xs px-1.5 py-0.5 rounded-md font-medium" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border-primary)' }}>
                {video.provider === WAND_VEGA_PROVIDER
                  ? `Wand Vega ${video.model === '1.5-pro' ? '1.5' : '1.0'} Pro`
                  : getSeedance25Capabilities(video.provider)?.retired ? getSeedance25Capabilities(video.provider).label : MODEL_CONFIGS[video.provider]?.name || getPublicVideoModelName(modelName)}
              </span>
              {video.referenceImages?.length > 0 && (
                <div className="flex -space-x-1.5">
                  {video.referenceImages.slice(0, 3).map((img, i) => (
                    <img key={i} src={img} alt="" className="w-5 h-5 rounded-full object-cover" style={{ border: '1px solid var(--color-bg-secondary)' }} />
                  ))}
                  {video.referenceImages.length > 3 && (
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-2xs" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)', border: '1px solid var(--color-bg-secondary)' }}>
                      +{video.referenceImages.length - 3}
                    </div>
                  )}
                </div>
              )}
              <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>{formatTime(video.createdAt)}</span>
            </div>
            <div className="generation-result-toolbar flex items-center gap-1">
              {video.status === 'success' && (
                <button onClick={() => onRetry(video)} className="p-1 rounded-lg generation-feedback hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-muted)' }} title="重新生成"><RefreshCw size={12} /></button>
              )}
              {canErase && (
                <button onClick={() => { setErasing(true); onEraseSubtitle?.(video, () => setErasing(false)); }} className="px-1.5 py-0.5 rounded-lg text-2xs generation-feedback hover:bg-[var(--color-red-light)]" style={{ color: 'var(--color-error)' }}>去字幕</button>
              )}
              {erasing && <span className="flex items-center gap-1 text-2xs" style={{ color: 'var(--color-error)' }}><Loader2 size={10} className="animate-spin" /></span>}
              {canCancel ? (
                <button onClick={() => onCancel?.(video)} className="px-1.5 py-0.5 rounded-lg text-2xs generation-feedback hover:bg-[var(--color-red-light)]" style={{ color: 'var(--color-error)' }}>取消</button>
              ) : !isPending ? (
                <button onClick={() => onDelete?.(video)} className="p-1 rounded-lg generation-feedback hover:bg-[var(--color-red-light)]" style={{ color: 'var(--color-text-muted)' }} title="删除"><Trash2 size={12} /></button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

// ── 进行中任务持久化（防刷新/切页丢失） ──
const PENDING_TASKS_KEY = 'video_agent_pending_tasks';

const normalizeVideoTaskCoordinate = (value) => {
  if (value == null || String(value).trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const loadPendingTasks = () => {
  try { return JSON.parse(localStorage.getItem(PENDING_TASKS_KEY) || '[]'); } catch { return []; }
};

const savePendingTask = (task) => {
  const list = loadPendingTasks().filter(t => t.id !== task.id);
  list.push(task);
  localStorage.setItem(PENDING_TASKS_KEY, JSON.stringify(list));
};

const removePendingTask = (taskId) => {
  const list = loadPendingTasks().filter(t => t.id !== taskId);
  localStorage.setItem(PENDING_TASKS_KEY, JSON.stringify(list));
};

// 失败任务没有对应的资源记录，单独保存参数快照，保证刷新/切页后仍可回填。
const FAILED_ATTEMPTS_KEY = 'video_agent_failed_attempts';

const loadFailedAttempts = () => {
  try { return JSON.parse(localStorage.getItem(FAILED_ATTEMPTS_KEY) || '[]'); } catch { return []; }
};

const saveFailedAttempt = (attempt) => {
  try {
    const videoId = String(attempt?.video?.id ?? '');
    if (!videoId) return;
    const list = loadFailedAttempts().filter(item => String(item?.video?.id ?? '') !== videoId);
    list.push(attempt);
    localStorage.setItem(FAILED_ATTEMPTS_KEY, JSON.stringify(list.slice(-100)));
  } catch (error) {
    console.warn('[VideoAgent] 保存失败任务参数快照失败:', error);
  }
};

const removeFailedAttempt = (videoId) => {
  try {
    const key = String(videoId ?? '');
    const list = loadFailedAttempts().filter(item => String(item?.video?.id ?? '') !== key);
    localStorage.setItem(FAILED_ATTEMPTS_KEY, JSON.stringify(list));
  } catch (error) {
    console.warn('[VideoAgent] 删除失败任务参数快照失败:', error);
  }
};

// ── 后处理任务持久化 ──
const PP_PENDING_TASKS_KEY = 'video_agent_pp_pending_tasks';

const loadPpPendingTasks = () => {
  try { return JSON.parse(localStorage.getItem(PP_PENDING_TASKS_KEY) || '[]'); } catch { return []; }
};

const savePpPendingTask = (task) => {
  const list = loadPpPendingTasks().filter(t => t.id !== task.id);
  list.push(task);
  localStorage.setItem(PP_PENDING_TASKS_KEY, JSON.stringify(list));
};

const removePpPendingTask = (taskId) => {
  const list = loadPpPendingTasks().filter(t => t.id !== taskId);
  localStorage.setItem(PP_PENDING_TASKS_KEY, JSON.stringify(list));
};

const getMediaNameFromUrl = (url, fallback) => {
  try {
    const clean = String(url || '').split('?')[0].split('#')[0];
    return decodeURIComponent(clean.split('/').pop() || fallback);
  } catch {
    return fallback;
  }
};

const buildGuaRefMetaFromItem = (item = {}, category = '') => ({
  id: item.id,
  label: item.label || item.name,
  name: item.label || item.name,
  displayName: item.displayName || item.name || item.label,
  type: item.type,
  typeCode: item.typeCode,
  source: item.source,
  sourceType: item.sourceType || category,
  category,
  originalUrl: item.originalUrl || item.originalImageUrl || item.sourceUrl,
  guaVideoAssetId: item.guaVideoAssetId,
  guaVideoAssetStatus: item.guaVideoAssetStatus,
  engGuaVideoAssetId: item.engGuaVideoAssetId,
  engGuaVideoAssetStatus: item.engGuaVideoAssetStatus,
  guaOminiAssetId: item.guaOminiAssetId,
  guaOminiAssetStatus: item.guaOminiAssetStatus,
  dreaminaProxyAssetId: item.dreaminaProxyAssetId,
  dreaminaProxyAssetStatus: item.dreaminaProxyAssetStatus,
  voiceSampleUrl: item.voiceSampleUrl,
  voiceSampleName: item.voiceSampleName,
  voiceBoundAt: item.voiceBoundAt,
});

// ── 主组件 ──
const VideoAgentModern = ({ projectId, episodeId }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const [projectRuntimeTick, setProjectRuntimeTick] = useState(0);
  const defaultProjectVideoProvider = useMemo(
    () => getProjectVideoSubmissionProvider(projectId, 'guavideo'),
    [projectId, projectRuntimeTick]
  );
  const isWan3ProjectRuntime = useMemo(
    () => defaultProjectVideoProvider === VIDEO_PROVIDER_WANX || isWan3Project(projectId),
    [defaultProjectVideoProvider, projectId, projectRuntimeTick]
  );
  const projectDefaultVideoRatio = useMemo(
    () => getProjectDefaultVideoRatio(projectId, getRememberedVideoRatio()),
    [projectId, projectRuntimeTick]
  );

  // 状态
  const [selectedModel, setSelectedModel] = useState(() => defaultProjectVideoProvider);
  const { statusMap } = useAssetStatusMap(projectId, { provider: selectedModel, enabled: isGuaLikeModel(selectedModel) });
  const [generationMode, setGenerationMode] = useState('ref2v');
  const [config, setConfig] = useState(() => ({
    ...(MODEL_CONFIGS[defaultProjectVideoProvider] || MODEL_CONFIGS.guavideo).defaults,
    aspectRatio: projectDefaultVideoRatio,
    ratio: projectDefaultVideoRatio,
  }));
  const [selectedLighting, setSelectedLighting] = useState(null); // 选中的打光预设 ID
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [images, setImages] = useState({ start: null, end: null, refs: [] });
  const [guaRefImages, setGuaRefImages] = useState([]);
  const [guaRefNames, setGuaRefNames] = useState(new Map()); // url → name 映射
  const [guaRefMeta, setGuaRefMeta] = useState(new Map()); // url → 角色/素材元数据
  const [guaRefVideos, setGuaRefVideos] = useState([]);
  const [wanxMedia, setWanxMedia] = useState([]);
  const [wanxLinkDraft, setWanxLinkDraft] = useState('');
  const [showWanxLinkInput, setShowWanxLinkInput] = useState(false);
  const [wanxUploading, setWanxUploading] = useState(false);
  const [videos, setVideos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showResourceSelector, setShowResourceSelector] = useState(false);
  const [showGuaRefSelector, setShowGuaRefSelector] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showFrameMenu, setShowFrameMenu] = useState(false); // 首帧/尾帧添加菜单
  const [frameTarget, setFrameTarget] = useState('start'); // 'start' | 'end' — 当前要添加的是首帧还是尾帧
  const [showFrameResourceSelector, setShowFrameResourceSelector] = useState(false); // 首帧/尾帧资源选择器
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showUsagePanel, setShowUsagePanel] = useState(false);
  const [mentionPopover, setMentionPopover] = useState(null); // { x, y, searchText, level, path, category, asset }
  const mentionIndexRef = useRef(0);
  const [menuItems, setMenuItems] = useState([]); // 当前层级的菜单项
  const [menuLoading, setMenuLoading] = useState(false);
  const [hoverPreview, setHoverPreviewRaw] = useState(null); // 参考图缩略图 hover 用
  const [lastHoverPreview, setLastHoverPreview] = useState(null); // 出场动画期间保留数据
  const setHoverPreview = useCallback((v) => {
    if (v) setLastHoverPreview(v);
    setHoverPreviewRaw(v);
  }, []);
  const [mentionPreviewUrl, setMentionPreviewUrl] = useState(null); // @ 菜单预览 URL
  const mentionUploadRef = useRef(null); // @ 本地上传用
  const mentionVideoUploadRef = useRef(null); // @ 本地上传参考视频用
  const mentionListRef = useRef(null); // @ 菜单滚动容器
  const mentionItemRefs = useRef([]); // @ 菜单每项 ref
  const mentionAtNodeRef = useRef(null); // @ 所在的文本节点
  const mentionAtIndexRef = useRef(-1); // @ 在文本节点中的 index

  // 后处理模式 state
  const [mode, setMode] = useState('generate'); // 'generate' | 'removeSubtitle'
  const [ppSourceVideo, setPpSourceVideo] = useState(null); // { url, name, provider? }
  const [ppTasks, setPpTasks] = useState([]);
  const [showPpResourceSelector, setShowPpResourceSelector] = useState(false);

  const { isAdmin } = useUser();

  const pollingMapRef = useRef(new Map());
  const wandSubmittingRef = useRef(false);
  const ppPollingMapRef = useRef(new Map());
  const assetPreflightGenerationRef = useRef(0);
  const scrollRef = useRef(null);
  const addMenuRef = useRef(null);
  const frameMenuRef = useRef(null);
  const editorRef = useRef(null);
  const guaImageInputRef = useRef(null);
  const guaVideoInputRef = useRef(null);
  const wanxFrameInputRef = useRef(null);
  const wanxImageInputRef = useRef(null);
  const wanxVideoInputRef = useRef(null);
  const wanxAudioInputRef = useRef(null);
  const wanxFileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const skipModelResetRef = useRef(false);
  const previousReferenceModelRef = useRef(selectedModel);
  const referenceDraftRef = useRef(null);
  referenceDraftRef.current = { images, guaRefImages, guaRefNames, guaRefMeta, guaRefVideos, wanxMedia };
  const draftContextKey = JSON.stringify([projectId, episodeId, selectedModel, generationMode]);
  const draftContextRef = useRef({ key: draftContextKey });
  if (draftContextRef.current.key !== draftContextKey) draftContextRef.current = { key: draftContextKey };
  const historyRequestRef = useRef(0);
  const historyScopeRef = useRef('');
  historyScopeRef.current = JSON.stringify([projectId, episodeId]);
  const ratioProjectKeyRef = useRef(String(projectId ?? ''));
  const manuallySelectedRatioProjectRef = useRef(null);

  const applyReferenceDraft = useCallback((draft) => {
    referenceDraftRef.current = draft;
    setImages(draft.images);
    setGuaRefImages(draft.guaRefImages);
    setGuaRefNames(draft.guaRefNames);
    setGuaRefMeta(draft.guaRefMeta);
    setGuaRefVideos(draft.guaRefVideos);
    setWanxMedia(draft.wanxMedia);
  }, []);

  // Mentions are optional prompt annotations, not the attachment source of truth.
  // Only an explicit attachment removal removes its corresponding prompt chips.
  const removeReferenceMention = useCallback((url) => {
    if (!url || !editorRef.current) return;
    let changed = false;
    editorRef.current.querySelectorAll('[data-mention]').forEach(chip => {
      if (chip.dataset.url === url) { chip.remove(); changed = true; }
    });
    if (changed) setPrompt(serializeEditor(editorRef.current));
  }, []);

  const modelConfig = MODEL_CONFIGS[selectedModel] || MODEL_CONFIGS.guavideo;
  // 运行时颜色：如果模型没有静态 color，用 theme 的 accentPrimary
  const mc = useMemo(() => ({
    ...modelConfig,
    ...(isDomesticOfficialSeedance(selectedModel) ? { resolutions: getDomesticSeedanceResolutions(config.model) } : {}),
    ...(selectedModel === WAND_VEGA_PROVIDER ? { durations: getWandVegaDurations(config.model) } : {}),
    color: modelConfig.color || theme.colors.accentPrimary,
  }), [modelConfig, selectedModel, config.model, theme.colors.accentPrimary]);
  const visibleModelConfigs = useMemo(() => (
    isWan3ProjectRuntime
      ? [MODEL_CONFIGS[VIDEO_PROVIDER_WANX], MODEL_CONFIGS[WAND_VEGA_PROVIDER]]
      : Object.values(MODEL_CONFIGS).filter((candidate) => (
        !getSeedance25Capabilities(candidate.id)?.retired
        && (!isGuaLikeModel(candidate.id) || isDomesticOfficialSeedance(candidate.id) || candidate.id === defaultProjectVideoProvider)
      ))
  ), [defaultProjectVideoProvider, isWan3ProjectRuntime]);

  useEffect(() => {
    if (!isDomesticOfficialSeedance(selectedModel)) return;
    setConfig((current) => {
      const resolution = normalizeDomesticSeedanceResolution(current.model, current.resolution);
      return resolution === current.resolution ? current : { ...current, resolution };
    });
  }, [selectedModel, config.model, config.resolution]);

  useEffect(() => {
    if (selectedModel !== WAND_VEGA_PROVIDER) return;
    setConfig((current) => {
      const value = String(current.resolution || '').trim().toLowerCase();
      const resolution = MODEL_CONFIGS[WAND_VEGA_PROVIDER].resolutions.includes(value) ? value : '1080p';
      return resolution === current.resolution ? current : { ...current, resolution };
    });
  }, [selectedModel, config.resolution]);

  useEffect(() => {
    const onProfileChange = (event) => {
      if (!projectId || String(event?.detail?.projectId) === String(projectId)) {
        setProjectRuntimeTick((value) => value + 1);
      }
    };
    window.addEventListener('project-runtime-profile-change', onProfileChange);
    return () => window.removeEventListener('project-runtime-profile-change', onProfileChange);
  }, [projectId]);

  useEffect(() => {
    const projectKey = String(projectId ?? '');
    if (ratioProjectKeyRef.current !== projectKey) {
      ratioProjectKeyRef.current = projectKey;
      manuallySelectedRatioProjectRef.current = null;
    }
    if (manuallySelectedRatioProjectRef.current === projectKey) return;
    setConfig((prev) => {
      const availableRatios = MODEL_CONFIGS[selectedModel]?.ratios || [];
      const nextRatio = availableRatios.length === 0 || availableRatios.includes(projectDefaultVideoRatio)
        ? projectDefaultVideoRatio
        : MODEL_CONFIGS[selectedModel]?.defaults?.aspectRatio || projectDefaultVideoRatio;
      if (prev.aspectRatio === nextRatio && prev.ratio === nextRatio) return prev;
      return { ...prev, aspectRatio: nextRatio, ratio: nextRatio };
    });
  }, [projectDefaultVideoRatio, projectId, selectedModel]);

  useEffect(() => {
    if (isWan3ProjectRuntime) {
      if (selectedModel !== VIDEO_PROVIDER_WANX && selectedModel !== WAND_VEGA_PROVIDER) {
        setSelectedModel(VIDEO_PROVIDER_WANX);
      }
      return;
    }
    if (!defaultProjectVideoProvider || !MODEL_CONFIGS[defaultProjectVideoProvider]) return;
    if (selectedModel && (!isGuaLikeModel(selectedModel) || isDomesticOfficialSeedance(selectedModel))) return;
    if (selectedModel === defaultProjectVideoProvider) return;
    setSelectedModel(defaultProjectVideoProvider);
    setConfig((prev) => ({
      ...MODEL_CONFIGS[defaultProjectVideoProvider].defaults,
      aspectRatio: prev.aspectRatio || projectDefaultVideoRatio,
      ratio: prev.ratio || projectDefaultVideoRatio,
    }));
  }, [defaultProjectVideoProvider, isWan3ProjectRuntime, projectDefaultVideoRatio, selectedModel]);

  // 更新 @ 菜单选中项（DOM 操作高亮，state 只更新预览 URL）
  const updateMentionIndex = useCallback((newIndex) => {
    const oldIndex = mentionIndexRef.current;
    mentionIndexRef.current = newIndex;

    // 更新高亮样式
    const oldItem = mentionItemRefs.current[oldIndex];
    const newItem = mentionItemRefs.current[newIndex];
    if (oldItem) {
      oldItem.style.backgroundColor = 'transparent';
      oldItem.style.color = 'var(--color-text-secondary)';
    }
    if (newItem) {
      const color = mc.color;
      newItem.style.backgroundColor = `${color}18`;
      newItem.style.color = color;
      newItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      const imgUrl = newItem.dataset.imageUrl;
      setMentionPreviewUrl(imgUrl || null);
    } else {
      setMentionPreviewUrl(null);
    }
  }, [mc.color]);

  // 加载历史
  useEffect(() => {
    if (projectId) loadHistory();
  }, [projectId, episodeId]);

  const loadHistory = async () => {
    const request = ++historyRequestRef.current;
    const scope = JSON.stringify([projectId, episodeId]);
    try {
      const allVideos = [];

      // 从 unified_resource 表查询 video_agent 来源的视频
      const resources = await authenticatedFetch(`${API_HOST}/api/resource/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: parseInt(projectId),
          ...(episodeId ? { episodeId: parseInt(episodeId) } : {}),
          source: 'video_agent',
          sortBy: 'created_at',
          sortOrder: 'desc',
        })
      });

      if (request !== historyRequestRef.current || scope !== historyScopeRef.current) return;

      if (Array.isArray(resources)) {
        // 只保留视频类型
        const videoResources = resources.filter(res => res.category === 'video' || res.typeCode?.includes('video'));
        const mapped = videoResources.map(res => {
          let metadata = {};
          try { metadata = typeof res.metadata === 'string' ? JSON.parse(res.metadata) : res.metadata || {}; } catch (e) {}
          return {
            id: res.id, localId: res.id, resourceId: res.id,
            resourceContext: { projectId, episodeId },
            status: 'success', progress: 100,
            videoUrl: res.url, prompt: metadata.prompt || res.prompt || '',
            model: metadata.model || 'unknown', provider: metadata.provider || 'guavideo',
            duration: res.duration ? `${res.duration}s` : null,
            createdAt: res.createdAt, generationParams: metadata,
            referenceImages: metadata.referenceImages || [],
            originalVideoUrl: metadata.originalVideoUrl || null,
          };
        });
        allVideos.push(...mapped);
      }

      allVideos.sort((a, b) => {
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return timeA - timeB;
      });

      // 恢复 localStorage 中进行中的任务，跳过已在后端完成的
      const completedIds = new Set(allVideos.map(v => String(v.id)));
      const failedAttempts = loadFailedAttempts().filter(item => {
        if (String(item?.projectId ?? '') !== String(projectId ?? '')) return false;
        return !episodeId || String(item?.episodeId ?? '') === String(episodeId);
      });
      failedAttempts.forEach(item => {
        const failedVideo = item?.video;
        if (!failedVideo?.id) return;
        if (completedIds.has(String(failedVideo.id))) {
          removeFailedAttempt(failedVideo.id);
          return;
        }
        allVideos.push(failedVideo);
      });

      const pendingTasks = loadPendingTasks().filter(t =>
        String(t.projectId) === String(projectId) && !completedIds.has(String(t.id))
      );
      // 清理已完成的 pending 记录
      loadPendingTasks().filter(t => completedIds.has(String(t.id))).forEach(t => removePendingTask(t.id));

      pendingTasks.forEach(task => {
        // 避免重复轮询
        if (pollingMapRef.current.has(task.id)) return;
        allVideos.push({ ...task.video, resourceContext: { projectId: task.projectId, episodeId: task.episodeId } });
        const stopPolling = startPolling(task.provider, task.id, {
          ...(task.provider === WAND_VEGA_PROVIDER ? { interval: 15000 } : {}),
          projectId: task.projectId ? parseInt(task.projectId) : undefined,
          episodeId: task.episodeId ? parseInt(task.episodeId) : undefined,
          prompt: task.prompt,
          model: task.model,
          subModel: task.subModel,
          duration: task.duration,
          mode: task.mode,
          sound: task.sound,
          resolution: task.resolution,
          hasReferenceVideo: task.hasReferenceVideo,
          onSuccess: (data) => {
            removePendingTask(task.id);
            removeFailedAttempt(task.id);
            setVideos(prev => prev.map(v => v.id === task.id ? { ...v, status: 'success', videoUrl: data.videoUrl, progress: 100, ...data } : v));
            if (toast?.success) toast.success('视频生成完成！');
            if (data.videoUrl && projectId) {
              const metadataWithOriginal = {
                ...task.video.generationParams,
                originalVideoUrl: data.originalVideoUrl || null,
              };
              authenticatedFetch(`${API_HOST}/api/resource/metadata`, {
                method: 'PATCH',
                body: JSON.stringify({ projectId: parseInt(projectId), videoUrl: data.videoUrl, metadata: JSON.stringify(metadataWithOriginal) }),
              }).catch(() => {});
            }
          },
          onError: (errorMsg) => {
            removePendingTask(task.id);
            const failedVideo = { ...task.video, status: 'failed', error: errorMsg };
            saveFailedAttempt({ projectId: task.projectId, episodeId: task.episodeId, video: failedVideo });
            setVideos(prev => prev.map(v => v.id === task.id ? failedVideo : v));
            if (toast?.error) toast.error(errorMsg);
          },
          onProgress: ({ progress }) => {
            setVideos(prev => prev.map(v => v.id === task.id ? { ...v, progress } : v));
          },
        });
        pollingMapRef.current.set(task.id, stopPolling);
      });

      // 重新排序（pending 任务排在最后）
      allVideos.sort((a, b) => {
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return timeA - timeB;
      });

      // 合并：保留当前 state 中正在 processing/queued 的任务（防止覆盖丢失）
      setVideos(prev => {
        const loadedIds = new Set(allVideos.map(v => String(v.id)));
        const inProgress = prev.filter(v =>
          (v.status === 'processing' || v.status === 'queued') && !loadedIds.has(String(v.id))
        );
        let merged = [...allVideos, ...inProgress];

        // ===== MOCK: 注入 100 个假视频用于性能测试（上线前删除）=====
        // 控制台执行: localStorage.setItem('VIDEO_PERF_TEST','1') 然后刷新
        // 关闭: localStorage.removeItem('VIDEO_PERF_TEST') 然后刷新
        if (localStorage.getItem('VIDEO_PERF_TEST')) {
          const MOCK_VIDEOS = [
            'https://www.w3schools.com/html/mov_bbb.mp4',
            'https://www.w3schools.com/html/movie.mp4',
            'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4',
          ];
          const mockItems = Array.from({ length: 100 }, (_, i) => ({
            id: `mock_${i}`,
            localId: `mock_${i}`,
            status: 'success',
            progress: 100,
            videoUrl: MOCK_VIDEOS[i % MOCK_VIDEOS.length],
            thumbnail: '',
            prompt: `性能测试视频 #${i + 1} — 这是一段用于测试 IntersectionObserver 虚拟化效果的假数据`,
            model: 'test',
            provider: ['guavideo', 'vidu', 'wangsuSora'][i % 3],
            duration: `${3 + (i % 12)}s`,
            createdAt: new Date(Date.now() - i * 60000).toISOString(),
            referenceImages: [],
          }));
          merged = [...merged, ...mockItems];
          console.log('[VideoAgent] 🧪 MOCK: 注入了 100 个测试视频，执行 localStorage.removeItem("VIDEO_PERF_TEST") 并刷新可关闭');
        }
        // ===== END MOCK =====

        return merged;
      });
      // 加载完滚到底部
      setTimeout(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'auto' }); }, 200);
    } catch (e) {
      console.error("加载视频历史失败:", e);
    }
  };

  // Only model-specific settings reset. Reference drafts survive model/mode changes.
  useEffect(() => {
    const previousModel = previousReferenceModelRef.current;
    previousReferenceModelRef.current = selectedModel;
    if (skipModelResetRef.current) {
      skipModelResetRef.current = false;
      return;
    }
    const nextModelConfig = MODEL_CONFIGS[selectedModel] || MODEL_CONFIGS.guavideo;
    setConfig((prev) => {
      const preferredRatio = prev.aspectRatio || prev.ratio;
      const nextRatio = nextModelConfig.ratios?.includes(preferredRatio)
        ? preferredRatio
        : nextModelConfig.defaults.aspectRatio;
      return {
        ...nextModelConfig.defaults,
        aspectRatio: nextRatio,
        ratio: nextRatio,
      };
    });
    setGenerationMode(prev => resolveVideoDraftMode(prev, nextModelConfig.modes));
    applyReferenceDraft(switchVideoReferenceBank(referenceDraftRef.current,
      getVideoReferenceBank(previousModel), getVideoReferenceBank(selectedModel)));
    setShowWanxLinkInput(false);
  }, [selectedModel]);

  useEffect(() => {
    return () => {
      draftContextRef.current = { ...draftContextRef.current };
      historyRequestRef.current += 1;
      assetPreflightGenerationRef.current += 1;
      pollingMapRef.current.forEach(stop => { if (typeof stop === 'function') stop(); });
      pollingMapRef.current.clear();
      ppPollingMapRef.current.forEach(stop => { if (typeof stop === 'function') stop(); });
      ppPollingMapRef.current.clear();
    };
  }, []);

  // 点击外部关闭添加菜单
  useEffect(() => {
    if (!showAddMenu) return;
    const handler = (e) => { if (addMenuRef.current && !addMenuRef.current.contains(e.target)) setShowAddMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAddMenu]);

  const updateConfig = (key, value) => setConfig(prev => ({ ...prev, [key]: value }));
  const handleVideoRatioChange = (value) => {
    manuallySelectedRatioProjectRef.current = String(projectId ?? '');
    setConfig((prev) => ({ ...prev, aspectRatio: value, ratio: value }));
  };

  // 图片上传（非 GuaVideo）— 先传 COS 再存 URL
  const handleImageUpload = async (type, file, inputElement) => {
    if (!file) return;
    if (selectedModel === WAND_VEGA_PROVIDER && !String(file.type || '').startsWith('image/')) {
      toast?.warning?.('Wand Vega 当前入口仅支持图片素材');
      if (inputElement) inputElement.value = '';
      return;
    }
    const uploadContext = draftContextRef.current;
    const seedance25Capabilities = getSeedance25Capabilities(selectedModel);
    if (type === 'refs' && seedance25Capabilities && images.refs.length >= seedance25Capabilities.maxReferenceImages) {
      toast?.warning?.(`${seedance25Capabilities.label} 最多支持 ${seedance25Capabilities.maxReferenceImages} 张参考图`);
      if (inputElement) inputElement.value = '';
      return;
    }
    try {
      const url = await uploadFileToCos(file);
      if (uploadContext !== draftContextRef.current) return;
      if (type === 'refs') {
        setImages(prev => ({ ...prev, refs: [...prev.refs, { id: Date.now(), url, name: file.name || '参考素材', mediaType: getSeedance25ReferenceMediaType({ type: file.type, url }) }] }));
      } else {
        setImages(prev => ({ ...prev, [type]: url }));
      }
    } catch (err) {
      toast?.error?.('图片上传失败: ' + err.message);
    } finally {
      if (inputElement) inputElement.value = '';
    }
  };

  const removeImage = (type, id) => {
    draftContextRef.current = { ...draftContextRef.current };
    const currentImages = referenceDraftRef.current.images;
    const url = type === 'refs' ? currentImages.refs.find(item => item.id === id)?.url : currentImages[type];
    const remainingRefs = type === 'refs' ? currentImages.refs.filter(item => item.id !== id) : currentImages.refs;
    if (!remainingRefs.some(item => item.url === url)) removeReferenceMention(url);
    if (type === 'refs') setImages(prev => ({ ...prev, refs: prev.refs.filter(r => r.id !== id) }));
    else setImages(prev => ({ ...prev, [type]: null }));
  };

  // ── 生成视频 ──
  const handleGenerate = async () => {
    try { assertSeedance25SubmissionProvider(selectedModel); } catch (failure) { setError(failure.message); return; }
    const submittedDraftContext = draftContextRef.current;
    if (!defaultProjectVideoProvider) {
      setError('项目视频厂商配置尚未加载，请刷新项目后重试');
      return;
    }
    if (!selectedModel || !MODEL_CONFIGS[selectedModel]) {
      setError('当前项目的视频服务配置无效');
      return;
    }
    const markedPrompt = getPromptWithMarkers();
    const guaImageItems = getGuaImageItems();
    // HappyHorse、GuaVideo 与万相分别使用各自的引用语法，其他模型直接删除标记。
    const refUrls = images.refs.filter(r => r.url).map(r => r.url);
    const isGuaLike = isGuaLikeModel(selectedModel);
    const isWanx = isWanxVideoProvider(selectedModel);
    const isWandVega = selectedModel === WAND_VEGA_PROVIDER;
    const seedance25Capabilities = getSeedance25Capabilities(selectedModel);
    const isSeedance25 = Boolean(seedance25Capabilities);
    const seedance25GenerationMode = isSeedance25 && generationMode === 'reference' && images.refs.length === 0
      ? 'text2video'
      : generationMode;
    const isSeedance25ReferenceMode = isSeedance25 && seedance25GenerationMode === 'reference';
    const submittedModel = isWanx
      ? normalizeWanxVideoModel(config.model)
      : isSeedance25 ? seedance25Capabilities.model : config.model;
    const effectiveResolution = isSeedance25
      ? normalizeSeedance25Resolution(selectedModel, config.resolution)
      : isDomesticOfficialSeedance(selectedModel)
      ? normalizeDomesticSeedanceResolution(submittedModel, config.resolution)
      : isWanx
      ? normalizeWanxVideoResolution(config.resolution, '1080P')
      : normalizeVideoResolutionForProvider(
        selectedModel,
        config.resolution,
        mc.defaults?.resolution || '480p',
      );
    const plainPrompt = (selectedModel === 'happyhorse' && generationMode === 'reference' && refUrls.length > 0)
      ? resolveRefMarkersToCharacter(markedPrompt, refUrls)
      : (isWanx && generationMode === 'reference')
        ? resolveWanxVideoMediaMarkers(markedPrompt, getActiveWanxDraftMedia(wanxMedia, generationMode))
      : (isGuaLike && generationMode === 'ref2v')
        ? resolveGuaVideoRefMarkers(markedPrompt, {
          images: guaRefImages,
          videos: guaRefVideos,
          audios: [],
          audioOffset: 0,
        })
        : stripRefMarkers(markedPrompt);
    // GuaVideo 去字幕：在提示词前注入指令
    let finalPrompt = (isGuaLike && config.noSubtitle)
      ? `禁止BGM，禁止字幕，如果出现字幕则字幕透明度设置成100%。${plainPrompt}`
      : plainPrompt;
    // 打光预设：在提示词前注入打光描述
    if (selectedLighting) {
      finalPrompt = applyLighting(finalPrompt, selectedLighting);
    }
    if (isGuaLike && generationMode === 'ref2v') {
      finalPrompt = applyGuaVoiceBindingPrompt(finalPrompt, [], {
        overseas: isOverseasVideoProvider(selectedModel),
      });
    }
    let wanxRequest = null;
    if (isWanx) {
      try {
        wanxRequest = validateWanxVideoRequest({
          model: submittedModel,
          prompt: finalPrompt,
          media: getActiveWanxDraftMedia(wanxMedia, generationMode),
          mode: generationMode,
          duration: config.duration,
          resolution: effectiveResolution,
          ratio: config.aspectRatio || config.ratio,
          seed: config.seed,
        });
      } catch (validationError) {
        setError(validationError.message);
        return;
      }
    }
    if (!plainPrompt.trim() && generationMode === 'text2video') { setError('请输入提示词'); return; }
    if (!isWanx && generationMode === 'start-end' && (!images.start || !images.end)) { setError('请上传首帧和尾帧图片'); return; }
    if (generationMode === 'img2video' && (isGuaLike || isWandVega) && !images.start) { setError('请上传首帧图片'); return; }
    if ((generationMode === 'img2video' || generationMode === 'subject-reference' || (!isSeedance25 && generationMode === 'reference')) && !isGuaLike && !isWanx && !(isWandVega && generationMode === 'img2video') && images.refs.length === 0) { setError('请上传参考图片'); return; }
    if (isGuaLike && generationMode === 'ref2v' && (guaRefImages.length + guaRefVideos.length) === 0) { setError('请添加至少一个参考素材'); return; }
    if (isGuaLike && (generationMode === 'edit-video' || generationMode === 'extend-video') && guaRefVideos.length === 0) { setError('请添加原视频'); return; }
    if (isSeedance25ReferenceMode) {
      const validation = validateSeedance25References(selectedModel, images.refs);
      if (validation.rejected.length > 0) {
        setError(`${seedance25Capabilities.label} 需要当前线路支持的原始素材 URL 或图片数据，不能使用其他厂商素材库 ID`);
        return;
      }
      if (images.refs.filter(item => getSeedance25ReferenceMediaType(item) === 'image').length > seedance25Capabilities.maxReferenceImages) {
        setError(`${seedance25Capabilities.label} 最多支持 ${seedance25Capabilities.maxReferenceImages} 张参考图`);
        return;
      }
      if (validation.accepted.length === 0) {
        setError(`${seedance25Capabilities.label} 参考生视频需要至少一个参考素材`);
        return;
      }
    }
    if (isSeedance25 && ['img2video', 'start-end'].includes(generationMode)) {
      const frames = generationMode === 'start-end' ? [images.start, images.end] : images.refs.slice(0, 1);
      if (validateSeedance25References(selectedModel, frames).rejected.length) {
        setError('首帧或尾帧缺少可用原始图片 URL，请重新选择原图；素材库 ID 不能替代图片');
        return;
      }
      if (frames.some(item => getSeedance25ReferenceMediaType(item) !== 'image')) {
        setError('首帧和尾帧只能使用图片；视频或音频请选择参考生成模式');
        return;
      }
    }
    if (isWandVega) {
      const version = String(config.model || '');
      if (!['1.0-pro', '1.5-pro'].includes(version)) {
        setError('请选择 Wand Vega 的 1.0 Pro 或 1.5 Pro 版本，不能自动切换到其他模型');
        return;
      }
      if (!MODEL_CONFIGS[WAND_VEGA_PROVIDER].modes.includes(generationMode)) {
        setError('当前 Wand Vega 生成模式不受支持，请重新选择生成模式');
        return;
      }
      if (!getWandVegaDurations(version).includes(config.duration)) {
        setError(`${version === '1.5-pro' ? '1.5 Pro' : '1.0 Pro'} 的可选时长为 ${version === '1.5-pro' ? '4–30' : '4–15'} 秒`);
        return;
      }
      if (!MODEL_CONFIGS[WAND_VEGA_PROVIDER].resolutions.includes(config.resolution)
        || !MODEL_CONFIGS[WAND_VEGA_PROVIDER].ratios.includes(config.aspectRatio)) {
        setError('Wand Vega 清晰度或画面比例无效；请重新选择');
        return;
      }
      const selectedMedia = generationMode === 'start-end'
        ? [images.start, images.end]
        : generationMode === 'img2video'
          ? [images.start]
          : generationMode === 'reference' ? images.refs : [];
      if (selectedMedia.some((item) => !isPublicWandMediaUrl(typeof item === 'string' ? item : item?.url))) {
        setError('Wand Vega 需要公网可访问的原始素材 URL；旧平台 asset://、本地地址或只有素材 ID 的引用不能提交');
        return;
      }
      if (selectedMedia.some((item) => typeof item !== 'string' && getSeedance25ReferenceMediaType(item) !== 'image')) {
        setError('当前 Wand Vega 入口仅支持图片参考，不支持视频或音频素材');
        return;
      }
    }

    if (isWandVega && wandSubmittingRef.current) return;
    if (isWandVega) wandSubmittingRef.current = true;
    setError(null);
    setIsSubmitting(true);
    const assetPreflightGeneration = assetPreflightGenerationRef.current + 1;
    assetPreflightGenerationRef.current = assetPreflightGeneration;

    const provider = mc.provider || mc.id;
    const taskCoordinateSnapshot = Object.freeze({
      projectId: normalizeVideoTaskCoordinate(projectId),
      episodeId: normalizeVideoTaskCoordinate(episodeId),
    });
    const normalizedSd25Duration = isSeedance25
      ? normalizeSeedance25Duration(selectedModel, config.duration)
      : null;
    const submittedDurationValue = isSeedance25
      ? `${normalizedSd25Duration}s`
      : config.duration;
    const normalizedSd25Ratio = isSeedance25
      ? normalizeSeedance25RatioForMode(selectedModel, seedance25GenerationMode, config.aspectRatio || config.ratio)
      : null;
    const refImgs = isGuaLike
      ? [...guaRefImages]
      : isWandVega && generationMode === 'start-end'
        ? [images.start, images.end].filter(Boolean)
      : isWandVega && generationMode === 'img2video'
        ? [images.start].filter(Boolean)
      : isSeedance25ReferenceMode
        ? (supportsSeedance25MediaReferences(selectedModel) ? images.refs.map(item => ({ ...item, url: getSeedance25ReferenceUrl(item) })) : normalizeSeedance25ReferenceUrls(selectedModel, images.refs))
      : isSeedance25 && generationMode === 'start-end'
        ? [images.start, images.end].filter(Boolean)
      : isSeedance25 && generationMode === 'img2video'
        ? images.refs.slice(0, 1).map(item => item.url).filter(Boolean)
      : isWanx
        ? wanxRequest.media.filter(item => [
          WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME,
          WANX_VIDEO_MEDIA_TYPES.LAST_FRAME,
          WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE,
        ].includes(item.type)).map(item => item.url)
        : images.refs.filter(r => r.url).map(r => r.url);
    const generationParams = {
      provider, selectedModel, generationMode: isSeedance25 ? seedance25GenerationMode : generationMode,
      model: submittedModel, duration: submittedDurationValue,
      aspectRatio: isSeedance25 ? normalizedSd25Ratio : config.aspectRatio,
      ratio: isSeedance25 ? normalizedSd25Ratio : config.ratio,
      resolution: effectiveResolution, prompt: plainPrompt,
      markedPrompt,
      configSnapshot: {
        ...config,
        model: submittedModel,
        duration: submittedDurationValue,
        aspectRatio: isSeedance25 ? normalizedSd25Ratio : config.aspectRatio,
        ratio: isSeedance25 ? normalizedSd25Ratio : config.ratio,
        resolution: effectiveResolution,
      },
      referenceImages: refImgs,
      generateAudio: config.generateAudio,
      webSearch: config.webSearch,
      noSubtitle: config.noSubtitle,
      selectedLighting,
      guaRefVideos: isGuaLike ? [...guaRefVideos] : [],
      guaRefNamesObj: isGuaLike ? Object.fromEntries(guaRefNames) : {},
      guaRefMetaObj: isGuaLike ? Object.fromEntries(guaRefMeta) : {},
      mode: config.mode, sound: config.sound, negativePrompt,
      audio: config.audio, seed: config.seed,
      watermark: config.watermark,
      wanxMedia: isWanx ? wanxRequest.media : [],
      startImage: images.start, endImage: images.end,
    };
    let submittedVideo = null;

    try {
      let taskId;

      if (generationMode === 'orbit' && selectedModel === 'vidu') {
        const res = await generateOrbitVideo(images.refs[0].url);
        taskId = res.taskId || res.task_id;
      } else if (generationMode === 'subject-reference' && selectedModel === 'minimax') {
        const res = await generateSubjectReference({
          model: config.model, prompt: finalPrompt,
          subjectReference: images.refs.filter(r => r.url).map(r => r.url),
          duration: parseInt(config.duration), aspectRatio: config.aspectRatio,
          promptOptimizer: config.promptOptimizer !== false,
        });
        taskId = res.taskId || res.task_id;
      } else {
        const payload = {
          provider, model: submittedModel, prompt: finalPrompt,
          duration: submittedDurationValue, aspectRatio: config.aspectRatio,
          ...taskCoordinateSnapshot,
        };

        if (isWandVega) {
          payload.generationMode = generationMode;
          payload.resolution = config.resolution;
          payload.generateAudio = config.generateAudio !== false;
          if (generationMode === 'img2video') payload.startImageUrl = images.start;
          if (generationMode === 'start-end') {
            payload.startImageUrl = images.start;
            payload.endImageUrl = images.end;
          }
          if (generationMode === 'reference') {
            payload.referenceUrls = images.refs.map((item) => item.url);
          }
        // 万相 3.0 专属：只提交 typed media，不借用 Seedance 素材库或旧 referenceUrls。
        } else if (isWanx) {
          payload.model = wanxRequest.model;
          payload.prompt = wanxRequest.prompt;
          payload.wanxMedia = wanxRequest.media.map(item => ({
            type: item.type,
            url: item.url,
            ...(item.duration != null ? { durationSeconds: item.duration } : {}),
          }));
          payload.duration = wanxRequest.duration;
          payload.resolution = wanxRequest.resolution;
          payload.ratio = wanxRequest.ratio;
          payload.aspectRatio = wanxRequest.ratio;
          payload.audio = config.audio !== false;
          payload.watermark = config.watermark === true;
          payload.promptExtend = config.promptExtend !== false;
          payload.mode = generationMode;
          payload.generationMode = seedance25GenerationMode;
          if (wanxRequest.seed != null) payload.seed = wanxRequest.seed;
        // Seedance 2.5：传原始素材定位当前库，由服务端核验并替换成当前库 Asset ID。
        } else if (isSeedance25) {
          payload.model = seedance25Capabilities.model;
          payload.duration = `${normalizedSd25Duration}s`;
          payload.aspectRatio = normalizedSd25Ratio;
          payload.ratio = payload.aspectRatio;
          payload.resolution = effectiveResolution;
          payload.generationMode = seedance25GenerationMode;
          if (seedance25Capabilities.supportsGenerateAudioControl) {
            payload.generateAudio = config.generateAudio !== false;
          }
          if (seedance25Capabilities.supportsWatermarkControl) {
            payload.watermark = config.watermark === true;
          }
          // 文生视频即使界面状态里还保留着上次的参考图，也不能把它们混进请求。
          if (isSeedance25ReferenceMode) {
            Object.assign(payload, buildSeedance25ReferencePayload(selectedModel, images.refs));
            payload.prompt = normalizeSeedance25ReferencePrompt(selectedModel, payload.prompt, images.refs);
          } else if (generationMode === 'img2video') {
            payload.startImageUrl = getSeedance25ReferenceUrl(images.refs[0]) || null;
          } else if (generationMode === 'start-end') {
            payload.startImageUrl = getSeedance25ReferenceUrl(images.start);
            payload.endImageUrl = getSeedance25ReferenceUrl(images.end);
          }
        // GuaVideo 专属
        } else if (isGuaLike) {
          const guaMode = generationMode === 'ref2v' ? 'ref2v'
            : generationMode === 'text2video' ? 't2v'
            : generationMode === 'edit-video' ? 'edit'
            : generationMode === 'extend-video' ? 'extend'
            : generationMode === 'start-end' ? 'i2v'
            : generationMode === 'img2video' ? 'i2v'
            : 'i2v';
          const guaContents = [];
          if (finalPrompt) guaContents.push({ type: 'text', text: finalPrompt });

          if (guaMode === 'i2v') {
            if (images.start || images.refs[0]) {
              const firstFrame = images.start || images.refs[0];
              guaContents.push({ type: 'image_url', url: isDreaminaProxySeedance20Provider(selectedModel)
                ? requireDreaminaProxyOriginalUrl(firstFrame) : firstFrame?.url || firstFrame, role: 'first_frame' });
            }
            if (images.end) guaContents.push({ type: 'image_url', url: isDreaminaProxySeedance20Provider(selectedModel)
              ? requireDreaminaProxyOriginalUrl(images.end) : images.end?.url || images.end, role: 'last_frame' });
          } else if (guaMode === 'ref2v') {
            guaImageItems.forEach(item => {
              const content = { type: 'image_url', url: isDreaminaProxySeedance20Provider(selectedModel)
                ? requireDreaminaProxyOriginalUrl(item)
                : isDomesticOfficialSeedance(selectedModel) ? getSeedance25ReferenceUrl(item) : item.url, role: 'reference_image' };
              const assetMeta = isDomesticOfficialSeedance(selectedModel) ? {} : getEffectiveAssetMeta(item, selectedModel);
              const activeAssetId = String(assetMeta.assetId || '').replace(/^asset:\/\//i, '');
              if (activeAssetId && String(assetMeta.status || '').toLowerCase() === 'active') {
                // Dreamina v3 re-ingests the original URL under its own account.
                // Never forward an old overseas library ID, even if marked Active.
                if (selectedModel === 'guavideo') content.assetId = activeAssetId;
              }
              guaContents.push(content);
            });
            guaRefVideos.forEach(url => guaContents.push({ type: 'video_url', url, role: 'reference_video' }));
          } else if (guaMode === 'edit') {
            // 编辑视频：原视频 + 编辑指令
            if (guaRefVideos.length > 0) guaContents.push({ type: 'video_url', url: guaRefVideos[0], role: 'reference_video' });
            else if (images.refs[0]?.url) guaContents.push({ type: 'video_url', url: images.refs[0].url, role: 'reference_video' });
          } else if (guaMode === 'extend') {
            // 延长视频：原视频作为参考
            if (guaRefVideos.length > 0) guaContents.push({ type: 'video_url', url: guaRefVideos[0], role: 'reference_video' });
            else if (images.refs[0]?.url) guaContents.push({ type: 'video_url', url: images.refs[0].url, role: 'reference_video' });
          }

          const preparedGuaContents = await preflightVideoAssetContents({
            provider,
            projectId,
            contents: guaContents,
            context: {
              episodeId,
              name: '视频 Agent 参考图',
              source: 'video_agent',
              sourceDetail: 'video_agent_preflight',
              typeCode: 'agent_image',
            },
            shouldContinue: () => assetPreflightGenerationRef.current === assetPreflightGeneration,
          });
          payload.guaVideoContents = preparedGuaContents;
          payload.prompt = preparedGuaContents.find(item => item.type === 'text')?.text || payload.prompt;
          payload.guaVideoMode = guaMode;
          payload.generateAudio = config.generateAudio ?? true;
          payload.ratio = config.aspectRatio || config.ratio || '9:16';
          payload.resolution = effectiveResolution;
          if (guaMode === 't2v' && config.webSearch) payload.guaWebSearch = true;
        } else {
          // 非 GuaVideo
          if (generationMode === 'img2video') {
            payload.startImageUrl = images.refs[0]?.url || null;
          } else if (generationMode === 'start-end') {
            payload.startImageUrl = images.start;
            payload.endImageUrl = images.end;
          } else if (generationMode === 'reference') {
            const refUrls = images.refs.filter(r => r.url).map(r => r.url);
            payload.referenceUrls = refUrls;
            payload.refCharacterImages = images.refs.filter(r => r.url).map((r, i) => ({ name: `角色${i + 1}`, base64: r.url }));

            if (selectedModel === 'happyhorse' && refUrls.length > 0) {
              payload.model = 'happyhorse-r2v';
              // prompt 已经通过 resolveRefMarkersToCharacter 自动替换了 characterN
              // 如果用户没用 @ 引用（prompt 里没有 character 标记），追加兜底
              if (!finalPrompt.includes('character')) {
                const charLabels = refUrls.map((_, i) => `character${i + 1}`).join('、');
                payload.prompt = `画面中出现${charLabels}，${finalPrompt}`;
              }
            }
          }

          // HappyHorse 专属
          if (selectedModel === 'happyhorse') {
            payload.resolution = config.resolution || '720P';
            // 根据子模型自动设置
            if (generationMode === 'img2video') payload.model = 'happyhorse-i2v';
            else if (generationMode === 'text2video') payload.model = 'happyhorse-t2v';
          }

          if (selectedModel === 'vidu') {
            payload.resolution = config.resolution;
            payload.audio = config.audio;
            if (config.seed != null) payload.seed = config.seed;
          }
        }

        const response = await (isWandVega ? submitWandVideoAgentTask(payload) : submitVideoTask(payload));
        taskId = response.taskId;
      }

      if (!taskId) throw new Error('未获取到任务ID');

      const newVideo = {
        id: taskId, localId: Date.now(), status: 'processing', progress: 0,
        resourceContext: { projectId, episodeId },
        prompt: plainPrompt,
        provider,
        model: submittedModel,
        duration: submittedDurationValue,
        referenceImages: refImgs,
        createdAt: new Date().toISOString(),
        generationParams,
      };
      submittedVideo = newVideo;
      const submittedDuration = isWanx
        ? wanxRequest.duration
        : isSeedance25
          ? normalizedSd25Duration
        : (parseInt(String(submittedDurationValue).replace(/[^0-9]/g, '')) || undefined);
      const submittedMode = isWanx || isWandVega ? generationMode : isSeedance25 ? seedance25GenerationMode : config.mode;
      const submittedSound = isSeedance25
        ? (seedance25Capabilities.supportsGenerateAudioControl && config.generateAudio === false ? 'off' : 'auto')
        : (isWanx ? config.audio : config.generateAudio) === false ? 'off' : 'on';
      const hasReferenceVideo = isWandVega
        ? false
        : isWanx
        ? wanxRequest.media.some(item => item.type === WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO)
        : isSeedance25
          ? isSeedance25ReferenceMode && (supportsSeedance25MediaReferences(selectedModel) ? images.refs.some(item => getSeedance25ReferenceMediaType(item) === 'video') : images.refs.length > 0)
          : !!(config.referenceVideo || guaRefVideos?.length || images.refs?.length);

      setVideos(prev => [...prev, newVideo]);
      // 持久化进行中的任务
      savePendingTask({
        id: taskId,
        provider,
        prompt: plainPrompt,
        model: submittedModel,
        subModel: isWanx
          ? wanxRequest.model
          : isSeedance25 ? seedance25Capabilities.model : (config.subModel || config.model),
        duration: submittedDuration,
        mode: submittedMode,
        sound: submittedSound,
        resolution: effectiveResolution,
        hasReferenceVideo,
        projectId: taskCoordinateSnapshot.projectId,
        episodeId: taskCoordinateSnapshot.episodeId,
        video: newVideo,
      });
      // 滚到底部
      setTimeout(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, 100);

      const stopPolling = startPolling(provider, taskId, {
        projectId: taskCoordinateSnapshot.projectId,
        episodeId: taskCoordinateSnapshot.episodeId,
        ...(isWandVega ? { interval: 15000 } : {}),
        prompt: plainPrompt,
        model: submittedModel,
        subModel: isWanx
          ? wanxRequest.model
          : isSeedance25 ? seedance25Capabilities.model : (config.subModel || config.model),
        duration: submittedDuration,
        mode: submittedMode,
        sound: submittedSound,
        resolution: effectiveResolution,
        hasReferenceVideo,
        onSuccess: (data) => {
          removePendingTask(taskId);
          removeFailedAttempt(taskId);
          setVideos(prev => prev.map(v => v.id === taskId ? { ...v, status: 'success', videoUrl: data.videoUrl, progress: 100, ...data } : v));
          if (toast?.success) toast.success('视频生成完成！');
          // 补全资源 metadata（含 originalVideoUrl），刷新后可恢复参数和去字幕功能
          if (data.videoUrl && projectId) {
            const metadataWithOriginal = {
              ...newVideo.generationParams,
              originalVideoUrl: data.originalVideoUrl || null,
            };
            const patchBody = {
              projectId: parseInt(projectId),
              videoUrl: data.videoUrl,
              metadata: JSON.stringify(metadataWithOriginal),
            };
            authenticatedFetch(`${API_HOST}/api/resource/metadata`, {
              method: 'PATCH',
              body: JSON.stringify(patchBody),
            }).catch(e => {
              console.warn('[VideoAgent] PATCH metadata 失败:', e.message);
            });
          }
        },
        onError: (errorMsg) => {
          removePendingTask(taskId);
          const failedVideo = { ...newVideo, status: 'failed', error: errorMsg };
          saveFailedAttempt({ projectId, episodeId, video: failedVideo });
          setVideos(prev => prev.map(v => v.id === taskId ? failedVideo : v));
          if (toast?.error) toast.error(errorMsg);
        },
        onProgress: ({ progress }) => {
          setVideos(prev => prev.map(v => v.id === taskId ? { ...v, progress } : v));
        }
      });

      pollingMapRef.current.set(taskId, stopPolling);
      setIsSubmitting(false);
      // A submit acknowledgement must not delete references or edits made while waiting.
      if (submittedDraftContext === draftContextRef.current && getPromptWithMarkers() === markedPrompt) {
        setPrompt('');
        if (editorRef.current) editorRef.current.innerHTML = '';
      }

    } catch (err) {
      console.error('生成失败:', err);
      const failedVideo = submittedVideo
        ? { ...submittedVideo, status: 'failed', error: err.message }
        : {
          id: `failed-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          localId: Date.now(),
          status: 'failed',
          progress: 0,
          prompt: plainPrompt,
          provider,
          model: submittedModel,
          duration: submittedDurationValue,
          referenceImages: refImgs,
          createdAt: new Date().toISOString(),
          generationParams,
          error: err.message,
        };
      removePendingTask(failedVideo.id);
      saveFailedAttempt({ projectId, episodeId, video: failedVideo });
      setVideos(prev => {
        const exists = prev.some(video => String(video.id) === String(failedVideo.id));
        return exists
          ? prev.map(video => String(video.id) === String(failedVideo.id) ? failedVideo : video)
          : [...prev, failedVideo];
      });
      setIsSubmitting(false);
      setError(`生成失败: ${err.message}`);
      if (toast?.error) toast.error(`生成失败: ${err.message}`);
    } finally {
      if (isWandVega) wandSubmittingRef.current = false;
    }
  };

  // 取消/删除/重试
  const handleCancel = async (video) => {
    try {
      await cancelTask(video.provider, video.id);
      const stop = pollingMapRef.current.get(video.id);
      if (stop) stop();
      removePendingTask(video.id);
      const failedVideo = { ...video, status: 'failed', error: '已取消' };
      saveFailedAttempt({ projectId, episodeId, video: failedVideo });
      setVideos(prev => prev.map(v => v.id === video.id ? failedVideo : v));
      if (toast?.success) toast.success('任务已取消');
    } catch (err) { if (toast?.error) toast.error(`取消失败: ${err.message}`); }
  };

  const handleDelete = (video) => setDeleteTarget(video);
  const confirmDeleteVideo = useCallback(async () => {
    if (!deleteTarget) return;
    const scope = historyScopeRef.current;
    const stored = deleteTarget.status === 'success';
    try {
      if (stored) await recycleStoredMedia({ ...deleteTarget.resourceContext, url: deleteTarget.videoUrl });
      if (scope !== historyScopeRef.current) return;
      historyRequestRef.current += 1;
      removePendingTask(deleteTarget.id);
      removeFailedAttempt(deleteTarget.id);
      setVideos(prev => prev.filter(video => video.id !== deleteTarget.id && !(stored && video.videoUrl === deleteTarget.videoUrl)));
      setDeleteTarget(null);
      toast.success(stored ? '视频已移入回收站，可联系管理员恢复。' : '已移除本地任务记录，未取消生成任务。');
    } catch (error) {
      toast.error(error?.message || '移入回收站失败，视频记录已保留。');
      throw error;
    }
  }, [deleteTarget, toast]);

  const handleRetry = (video) => {
    if (!video.generationParams) { if (toast?.warning) toast.warning('生成参数已丢失'); return; }
    const params = video.generationParams;
    draftContextRef.current = { ...draftContextRef.current };

    // 1. 恢复模型选择；项目绑定的视频服务不可被历史任务改写
    const persistedSeedance25Provider = normalizeSeedance25SubmissionProvider(resolveSeedance25Provider({
      provider: params.provider || params.selectedModel,
      model: params.model || params.subModel,
    }));
    const requestedModelKey = persistedSeedance25Provider || ((params.selectedModel && MODEL_CONFIGS[params.selectedModel])
      ? params.selectedModel
      : (Object.keys(MODEL_CONFIGS).find(k => MODEL_CONFIGS[k].id === params.provider)
        || (MODEL_CONFIGS[params.provider] ? params.provider : 'guavideo')));
    const modelKey = isWan3ProjectRuntime && requestedModelKey !== WAND_VEGA_PROVIDER
      ? VIDEO_PROVIDER_WANX
      : isGuaLikeModel(requestedModelKey) && !isDomesticOfficialSeedance(requestedModelKey)
        ? defaultProjectVideoProvider
        : requestedModelKey;
    if (!modelKey || !MODEL_CONFIGS[modelKey]) {
      if (toast?.warning) toast.warning('当前项目的视频服务配置无效');
      return;
    }
    // 同一模型不会触发 selectedModel effect，不能把“跳过一次重置”留给下次手动切换。
    skipModelResetRef.current = modelKey !== selectedModel;
    setSelectedModel(modelKey);

    // 2. 恢复生成模式
    setGenerationMode(resolveVideoDraftMode(params.generationMode, MODEL_CONFIGS[modelKey].modes));

    // 3. 恢复配置参数
    const defaultConfig = MODEL_CONFIGS[modelKey]?.defaults || {};
    const configSnapshot = params.configSnapshot || {};
    const providerChanged = isGuaLikeModel(requestedModelKey) && requestedModelKey !== modelKey;
    const restoringSeedance25 = getSeedance25Capabilities(modelKey);
    const restoredRatio = restoringSeedance25
      ? normalizeSeedance25Ratio(modelKey,
        params.aspectRatio || params.ratio || configSnapshot.aspectRatio || configSnapshot.ratio,
      )
      : (params.aspectRatio || params.ratio || configSnapshot.aspectRatio || configSnapshot.ratio || '9:16');
    const restoredConfig = {
      ...defaultConfig,
      ...configSnapshot,
      model: restoringSeedance25
        ? restoringSeedance25.model
        : isWanxVideoProvider(modelKey)
        ? normalizeWanxVideoModel(params.model || configSnapshot.model)
        : providerChanged ? defaultConfig.model : (params.model || configSnapshot.model || defaultConfig.model),
      duration: restoringSeedance25
        ? `${normalizeSeedance25Duration(modelKey, params.duration || configSnapshot.duration || defaultConfig.duration)}s`
        : (params.duration || configSnapshot.duration || defaultConfig.duration),
      aspectRatio: restoredRatio,
      ratio: restoredRatio,
      resolution: restoringSeedance25
        ? normalizeSeedance25Resolution(
          modelKey,
          params.resolution || configSnapshot.resolution || defaultConfig.resolution,
        )
        : isWanxVideoProvider(modelKey)
        ? normalizeWanxVideoResolution(
          params.resolution || configSnapshot.resolution || defaultConfig.resolution,
          '1080P',
        )
        : normalizeVideoResolutionForProvider(
          modelKey,
          params.resolution || configSnapshot.resolution || defaultConfig.resolution,
          defaultConfig.resolution || '480p',
        ),
      generateAudio: params.generateAudio ?? configSnapshot.generateAudio ?? true,
      webSearch: params.webSearch ?? configSnapshot.webSearch ?? false,
      noSubtitle: params.noSubtitle ?? configSnapshot.noSubtitle ?? false,
      mode: params.mode ?? configSnapshot.mode ?? defaultConfig.mode,
      sound: params.sound ?? configSnapshot.sound ?? defaultConfig.sound,
      audio: params.audio ?? configSnapshot.audio ?? true,
      seed: params.seed ?? configSnapshot.seed ?? null,
      watermark: params.watermark ?? configSnapshot.watermark ?? false,
    };
    setConfig(restoredConfig);
    setSelectedLighting(params.selectedLighting || null);

    // 4. 恢复提示词（优先用带 @ 标记的 markedPrompt，可还原 chip）
    const promptToRestore = params.markedPrompt || params.prompt || '';
    setPrompt(promptToRestore);
    const targetColor = MODEL_CONFIGS[modelKey]?.color || theme.colors.accentPrimary;
    renderPromptToEditor(editorRef.current, promptToRestore, targetColor);

    // 5. 恢复负向提示词
    setNegativePrompt(params.negativePrompt || '');

    // 6. 恢复参考图/视频
    const draft = switchVideoReferenceBank(referenceDraftRef.current,
      getVideoReferenceBank(selectedModel), getVideoReferenceBank(modelKey));
    applyReferenceDraft(restoreVideoReferenceDraft(draft, params, getVideoReferenceBank(modelKey)));
    setShowWanxLinkInput(false);

    if (toast?.info) toast.info('已恢复参数，请点击生成');
  };

  // ── 后处理 handler ──
  const handlePostProcess = async () => {
    if (!ppSourceVideo?.url) { setError('请选择源视频'); return; }
    setError(null);
    setIsSubmitting(true);

    try {
      const taskType = 'erase-subtitle';
      const response = await submitEraseSubtitle({ videoUrl: ppSourceVideo.url });

      const taskId = response.taskId;
      const newTask = {
        id: taskId,
        type: taskType,
        status: 'processing',
        sourceVideoUrl: ppSourceVideo.url,
        sourceVideoName: ppSourceVideo.name || '视频',
        resultVideoUrl: null,
        config: {},
        createdAt: new Date().toISOString(),
        error: null,
        internalTaskId: response.internalTaskId,
        projectId,
        episodeId,
        context: { origin: 'panel' },
      };

      setPpTasks(prev => [...prev, newTask]);
      savePpPendingTask({
        id: taskId,
        type: taskType,
        task: newTask,
        projectId,
        episodeId,
      });

      const stopPolling = startPostProcessPolling(taskType, taskId, {
        interval: 5000,
        projectId: projectId ? parseInt(projectId) : undefined,
        episodeId: episodeId ? parseInt(episodeId) : undefined,
        onSuccess: (data) => {
          removePpPendingTask(taskId);
          ppPollingMapRef.current.delete(taskId);
          setPpTasks(prev => prev.map(t =>
            t.id === taskId ? { ...t, status: 'success', resultVideoUrl: data.videoUrl } : t
          ));
          toast?.success('去字幕完成');
        },
        onError: (msg) => {
          removePpPendingTask(taskId);
          ppPollingMapRef.current.delete(taskId);
          setPpTasks(prev => prev.map(t =>
            t.id === taskId ? { ...t, status: 'failed', error: msg } : t
          ));
          toast?.error(msg);
        },
        onProgress: () => {},
      });

      ppPollingMapRef.current.set(taskId, stopPolling);
      setIsSubmitting(false);
      setPpSourceVideo(null);
    } catch (err) {
      setIsSubmitting(false);
      setError(`处理失败: ${err.message}`);
      toast?.error(err.message);
    }
  };

  const handlePpDelete = (task) => {
    setPpTasks(prev => prev.filter(t => t.id !== task.id));
    removePpPendingTask(task.id);
    const stop = ppPollingMapRef.current.get(task.id);
    if (stop) { stop(); ppPollingMapRef.current.delete(task.id); }
  };

  const handlePpRetry = (task) => {
    if (task.type !== 'erase-subtitle') return;
    setPpSourceVideo({ url: task.sourceVideoUrl, name: task.sourceVideoName });
    setMode('removeSubtitle');
    toast?.info('已恢复参数，请点击提交');
  };

  // 后处理视频本地上传
  const handlePpVideoUpload = async (file) => {
    if (!file) return;
    if (file.size > 500 * 1024 * 1024) { toast?.warning('视频不能超过500MB'); return; }
    try {
      const url = await uploadFileMultipart(file);
      setPpSourceVideo({ url, name: file.name.split('.')[0] || '本地视频' });
    } catch (err) {
      toast?.error('上传失败: ' + err.message);
    }
  };

  // 从 VideoCard 触发去字幕
  const handleEraseSubtitle = async (video, onDone) => {
    if (!video.originalVideoUrl) {
      toast?.error('缺少原始视频 URL，无法去字幕');
      onDone?.();
      return;
    }
    try {
      const response = await submitEraseSubtitle({ videoUrl: video.originalVideoUrl });
      const taskId = response.taskId;

      toast?.info('去字幕任务已提交，正在处理...');

      startPostProcessPolling('erase-subtitle', taskId, {
        interval: 5000,
        onSuccess: (data) => {
          const resourceId = video.resourceId ?? video.id;
          setVideos(prev => prev.map(v => (
            String(v.resourceId ?? v.id) === String(resourceId)
              ? { ...v, videoUrl: data.videoUrl }
              : v
          )));
          authenticatedFetch(`${API_HOST}/api/resource/update-url`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ resourceId, newUrl: data.videoUrl }),
          }).catch(error => console.warn('更新资源URL失败:', error));
          toast?.success('去字幕完成');
          onDone?.();
        },
        onError: (msg) => {
          toast?.error('去字幕失败: ' + msg);
          onDone?.();
        },
        onProgress: () => {},
      });

      // 可选：存储 stopPolling 到 ref，以便组件卸载时清理
      // ppPollingMapRef.current.set(taskId, stopPolling);
    } catch (err) {
      toast?.error('提交去字幕任务失败: ' + err.message);
      onDone?.();
    }
  };

  // 恢复 localStorage 中的后处理任务
  useEffect(() => {
    const pendingPpTasks = loadPpPendingTasks().filter(saved => (
      saved.type === 'erase-subtitle'
      && (saved.projectId == null || String(saved.projectId) === String(projectId))
    ));
    if (pendingPpTasks.length === 0) return;

    pendingPpTasks.forEach(saved => {
      if (ppPollingMapRef.current.has(saved.id)) return;
      setPpTasks(prev => {
        if (prev.some(t => t.id === saved.id)) return prev;
        return [...prev, saved.task];
      });
      const stopPolling = startPostProcessPolling(saved.type, saved.id, {
        interval: 5000,
        projectId: saved.projectId ? parseInt(saved.projectId) : (projectId ? parseInt(projectId) : undefined),
        episodeId: saved.episodeId ? parseInt(saved.episodeId) : (episodeId ? parseInt(episodeId) : undefined),
        onSuccess: (data) => {
          removePpPendingTask(saved.id);
          ppPollingMapRef.current.delete(saved.id);
          setPpTasks(prev => prev.map(t =>
            t.id === saved.id ? { ...t, status: 'success', resultVideoUrl: data.videoUrl } : t
          ));
          toast?.success('去字幕完成');
        },
        onError: (msg) => {
          removePpPendingTask(saved.id);
          ppPollingMapRef.current.delete(saved.id);
          setPpTasks(prev => prev.map(t =>
            t.id === saved.id ? { ...t, status: 'failed', error: msg } : t
          ));
          toast?.error(msg);
        },
        onProgress: () => {},
      });
      ppPollingMapRef.current.set(saved.id, stopPolling);
    });
  }, [episodeId, projectId, toast]);

  // 按日期分组
  const groupedVideos = useMemo(() => {
    const groups = {};
    videos.forEach(v => {
      const key = getDateKey(v.createdAt);
      if (!groups[key]) groups[key] = { label: formatDate(v.createdAt), videos: [] };
      groups[key].videos.push(v);
    });
    return Object.values(groups);
  }, [videos]);

  const isGuaVideo = isGuaLikeModel(selectedModel);
  const isWanxVideo = isWanxVideoProvider(selectedModel);
  const isWandVegaVideo = selectedModel === WAND_VEGA_PROVIDER;
  const vegaCostRequest = isWandVegaVideo ? {
    type: 'video',
    model: WAND_VEGA_PROVIDER,
    subModel: config.model,
    resolution: config.resolution,
    durationSeconds: Number.parseInt(config.duration, 10),
    count: 1,
    source: 'video_agent',
  } : null;
  const activeSeedance25Capabilities = getSeedance25Capabilities(selectedModel);
  const isSeedance25 = Boolean(activeSeedance25Capabilities);
  const activeVideoRatio = isSeedance25
    ? normalizeSeedance25RatioForMode(selectedModel, generationMode, config.aspectRatio || config.ratio)
    : config.aspectRatio || config.ratio || '9:16';
  const activeVideoRatios = isSeedance25
    ? getSeedance25RatiosForMode(selectedModel, generationMode)
    : mc.ratios || ['16:9', '9:16', '1:1'];
  const wanxFirstFrame = wanxMedia.find(item => item.type === WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME) || null;
  const wanxLastFrame = wanxMedia.find(item => item.type === WANX_VIDEO_MEDIA_TYPES.LAST_FRAME) || null;
  const wanxReferenceMedia = wanxMedia.filter(item => ![
    WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME,
    WANX_VIDEO_MEDIA_TYPES.LAST_FRAME,
  ].includes(item.type));
  const wanxHasFileOrLink = wanxReferenceMedia.some(item => [
    WANX_VIDEO_MEDIA_TYPES.FILE,
    WANX_VIDEO_MEDIA_TYPES.LINK,
  ].includes(item.type));

  const rememberGuaRefMeta = useCallback((url, item = {}, category = '') => {
    if (!url) return;
    setGuaRefMeta(prev => new Map(prev).set(url, buildGuaRefMetaFromItem(item, category)));
  }, []);

  const removeGuaRefMeta = useCallback((url) => {
    setGuaRefMeta(prev => {
      const next = new Map(prev);
      next.delete(url);
      return next;
    });
  }, []);

  const getGuaImageItems = useCallback(() => (
    guaRefImages.map((url) => ({
      url,
      name: guaRefNames.get(url),
      ...(guaRefMeta.get(url) || {}),
    }))
  ), [guaRefImages, guaRefNames, guaRefMeta]);

  // ── @ 提及：序列化 editor（带标记） ──
  const getPromptWithMarkers = useCallback(() => serializeEditor(editorRef.current), []);

  const addWanxMediaItem = useCallback((item) => {
    const isFrame = [WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME, WANX_VIDEO_MEDIA_TYPES.LAST_FRAME].includes(item.type);
    const currentMedia = referenceDraftRef.current.wanxMedia;
    const withoutReplacedFrame = isFrame ? currentMedia.filter(mediaItem => mediaItem.type !== item.type) : currentMedia;
    if (!isFrame && withoutReplacedFrame.some(mediaItem => mediaItem.type === item.type && mediaItem.url === item.url)) {
      toast?.warning('该素材已经添加');
      return false;
    }
    const nextMedia = [...withoutReplacedFrame, item];
    try {
      const selectionMode = isFrame ? 'start-end' : 'reference';
      assertWanxVideoMediaSelection(getActiveWanxDraftMedia(nextMedia, selectionMode), null, config.duration);
      referenceDraftRef.current = { ...referenceDraftRef.current, wanxMedia: nextMedia };
      setWanxMedia(nextMedia);
      setError(null);
      return true;
    } catch (validationError) {
      toast?.warning(validationError.message);
      return false;
    }
  }, [config.duration, toast]);

  const removeWanxMediaItem = useCallback((id) => {
    draftContextRef.current = { ...draftContextRef.current };
    const currentMedia = referenceDraftRef.current.wanxMedia;
    const url = currentMedia.find(item => item.id === id)?.url;
    if (!currentMedia.some(item => item.id !== id && item.url === url)) removeReferenceMention(url);
    setWanxMedia(prev => prev.filter(item => item.id !== id));
  }, [removeReferenceMention]);

  const handleGenerationModeChange = useCallback((nextMode) => {
    if (nextMode === generationMode) return;
    draftContextRef.current = { ...draftContextRef.current };
    setGenerationMode(nextMode);
    setShowWanxLinkInput(false);
  }, [generationMode]);

  // ── @ 提及：数据源 ──
  const mentionSource = useMemo(() => {
    if (isGuaVideo && generationMode === 'ref2v') {
      return [
        ...guaRefImages.map((url, i) => ({ id: `gua-image-${i}`, name: guaRefNames.get(url) || `图片${i + 1}`, url, category: 'image', searchText: `图片 图 ${i + 1}` })),
        ...guaRefVideos.map((url, i) => ({ id: `gua-video-${i}`, name: guaRefNames.get(url) || `视频${i + 1}`, url, category: 'video', searchText: `视频 video ${i + 1}` })),
      ];
    }
    if (isWanxVideo && generationMode === 'reference') {
      return wanxReferenceMedia
        .filter(item => [
          WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE,
          WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO,
          WANX_VIDEO_MEDIA_TYPES.REFERENCE_AUDIO,
        ].includes(item.type))
        .map(item => {
          const label = getWanxVideoMediaLabel(item, wanxMedia);
          return {
            id: item.id,
            name: item.name || label,
            url: item.url,
            category: item.type,
            searchText: `${label} ${item.name || ''}`,
          };
        });
    }
    if (selectedModel === WAND_VEGA_PROVIDER && ['img2video', 'start-end'].includes(generationMode)) {
      return [
        ...(images.start ? [{ id: 'wand-first-frame', name: '首帧', url: images.start, category: 'image' }] : []),
        ...(generationMode === 'start-end' && images.end ? [{ id: 'wand-last-frame', name: '尾帧', url: images.end, category: 'image' }] : []),
      ];
    }
    return images.refs.map((r, i) => ({ id: r.id, name: `${{ image: '图片', video: '视频', audio: '音频' }[getSeedance25ReferenceMediaType(r)]}${i + 1}`, url: r.url }));
  }, [isGuaVideo, isWanxVideo, selectedModel, generationMode, guaRefImages, guaRefVideos, guaRefNames, images.start, images.end, images.refs, wanxMedia, wanxReferenceMedia]);

  // 过滤后的 mention 列表（兼容旧逻辑 + 多级菜单）
  const filteredMentions = useMemo(() => {
    if (!mentionPopover) return [];
    const q = mentionPopover.searchText || '';
    // 多级菜单模式：root 级显示已有参考图 + 固定入口
    if (mentionPopover.level === 'root') {
      let existingItems = [];
      if (isGuaVideo && generationMode === 'ref2v') {
        existingItems = [
          ...guaRefImages.map((url, i) => ({
            id: `existing-image-${i}`, icon: null, imageUrl: url, url,
            label: guaRefNames.get(url) || `图片${i + 1}`,
            action: 'select-existing', category: 'image', searchText: `图片 图 ${i + 1}`,
          })),
          ...guaRefVideos.map((url, i) => ({
            id: `existing-video-${i}`, icon: Video, imageUrl: null, url,
            label: guaRefNames.get(url) || `视频${i + 1}`,
            action: 'select-existing', category: 'video', searchText: `视频 video ${i + 1} ${getMediaNameFromUrl(url, '')}`,
          })),
        ];
      } else if (isWanxVideo && generationMode === 'reference') {
        existingItems = mentionSource.map(item => ({
          id: `existing-${item.id}`,
          icon: item.category === WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO
            ? Video
            : item.category === WANX_VIDEO_MEDIA_TYPES.REFERENCE_AUDIO ? Music : null,
          imageUrl: item.category === WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE ? item.url : null,
          url: item.url,
          label: item.name,
          action: 'select-existing',
          category: item.category,
          searchText: item.searchText,
        }));
      }
      const uploadItems = ((isGuaVideo && generationMode === 'ref2v') || (isWanxVideo && generationMode === 'reference'))
        ? [
          { id: 'upload-image', icon: ImageIcon, label: '上传图片', action: 'upload-image', searchText: '图片 图 image upload' },
          { id: 'upload-video', icon: Video, label: '上传视频', action: 'upload-video', searchText: '视频 video upload' },
        ]
        : [{ id: 'upload', icon: Upload, label: '本地上传', action: 'upload' }];
      const rootItems = [
        ...existingItems,
        { id: 'resource', icon: Folder, label: '资源管理器', action: 'navigate', nextLevel: 'source' },
        ...uploadItems,
      ];
      const matches = (item) => `${item.label || ''} ${item.name || ''} ${item.searchText || ''}`.toLowerCase().includes(q);
      return q ? rootItems.filter(matches) : rootItems;
    }
    // source / assets / variants 级：用 menuItems
    if (['source', 'assets', 'variants'].includes(mentionPopover.level)) {
      return q ? menuItems.filter(i => i.label.toLowerCase().includes(q)) : menuItems;
    }
    // fallback：旧逻辑
    return mentionSource.filter(m => !q || `${m.name || ''} ${m.searchText || ''}`.toLowerCase().includes(q));
  }, [mentionPopover, mentionSource, menuItems, isGuaVideo, isWanxVideo, generationMode, guaRefImages, guaRefVideos, guaRefNames]);
  const visibleMentions = useMemo(() => filteredMentions.slice(0, MENTION_RENDER_LIMIT), [filteredMentions]);
  const hiddenMentionCount = Math.max(0, filteredMentions.length - visibleMentions.length);

  // ── 出场动画：菜单/弹层延迟卸载 ──
  const usagePanelMount = useDelayedUnmount(showUsagePanel, 150);
  const frameMenuMount = useDelayedUnmount(showFrameMenu, 150);
  const addMenuMount = useDelayedUnmount(showAddMenu, 150);
  const mentionMenuMount = useDelayedUnmount(!!mentionPopover && (filteredMentions.length > 0 || menuLoading), 150);
  const hoverPreviewMount = useDelayedUnmount(!!hoverPreview, 150);
  const activeHoverPreview = hoverPreview || lastHoverPreview;

  // 菜单关闭时清预览
  useEffect(() => {
    if (!mentionPopover) setMentionPreviewUrl(null);
  }, [mentionPopover]);

  // ── @ 提及：检测输入 ──
  const handleEditorInput = useCallback(() => {
    // 同步 prompt 状态
    setPrompt(getPromptWithMarkers());

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) { setMentionPopover(null); return; }

    const range = sel.getRangeAt(0);
    let cursorNode = range.startContainer;
    let cursorOffset = range.startOffset;

    // 光标必须在文本节点中才可能有 @
    if (cursorNode.nodeType !== Node.TEXT_NODE) { setMentionPopover(null); return; }

    // 只在当前文本节点内找 @，不跨 chip
    const textBefore = cursorNode.textContent.substring(0, cursorOffset);
    const lastAt = textBefore.lastIndexOf('@');
    if (lastAt !== -1) {
      const afterAt = textBefore.substring(lastAt + 1);
      if (!afterAt.includes(' ') && !afterAt.includes('\n')) {
        const rect = editorRef.current.getBoundingClientRect();
        // @ 触发时打开 root 级菜单，并保存 @ 所在节点和位置
        setMentionPopover({ x: rect.left + 10, y: rect.top, searchText: afterAt.toLowerCase(), level: 'root', path: [] });
        mentionAtNodeRef.current = cursorNode;
        mentionAtIndexRef.current = lastAt;
        mentionIndexRef.current = 0;
        setMenuItems([]);
        return;
      }
    }
    setMentionPopover(null);
  }, [getPromptWithMarkers]);

  // ── @ 提及：选中插入 chip ──
  const handleMentionSelect = useCallback((item) => {
    if (!mentionPopover || !editorRef.current) return;

    const atNode = mentionAtNodeRef.current;
    const atIdx = mentionAtIndexRef.current;

    // 用保存的 @ 节点定位
    if (!atNode || atIdx < 0 || !atNode.parentNode) {
      setMentionPopover(null);
      mentionAtNodeRef.current = null;
      mentionAtIndexRef.current = -1;
      return;
    }

    // 只删除 @ + 搜索文字，不多删
    const searchLen = (mentionPopover.searchText || '').length;
    const endOffset = Math.min(atIdx + 1 + searchLen, (atNode.textContent || '').length);

    // 删除 @searchText
    const deleteRange = document.createRange();
    deleteRange.setStart(atNode, atIdx);
    deleteRange.setEnd(atNode, endOffset);
    deleteRange.deleteContents();

    // deleteContents 后 atNode 文本变为 "前面" + "后面"（合在一起）
    // 在 atIdx 位置拆分，确保 chip 插入到正确位置
    const chip = createMentionChip(item.name, item.url, mc.color || theme.colors.accentPrimary, item.category || '');
    const afterNode = atNode.textContent.length > atIdx ? atNode.splitText(atIdx) : null;
    if (afterNode) {
      atNode.parentNode.insertBefore(chip, afterNode);
    } else if (atNode.parentNode) {
      atNode.parentNode.insertBefore(chip, atNode.nextSibling);
    } else {
      editorRef.current.appendChild(chip);
    }

    const space = document.createTextNode('\u00A0');
    chip.after(space);

    // 光标移到空格后
    const sel = window.getSelection();
    const newRange = document.createRange();
    newRange.setStartAfter(space);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);

    mentionAtNodeRef.current = null;
    mentionAtIndexRef.current = -1;

    setMentionPopover(null);
    setPrompt(getPromptWithMarkers());
  }, [mentionPopover, mc.color, theme.colors.accentPrimary, getPromptWithMarkers]);

  // ── @ 多级菜单：导航到下一级 ──
  const handleMenuNavigate = useCallback(async (item) => {
    if (!mentionPopover) return;
    const prev = mentionPopover;

    if (item.action === 'upload' || item.action === 'upload-image') {
      // 本地上传
      mentionUploadRef.current?.click();
      return;
    }
    if (item.action === 'upload-video') {
      mentionVideoUploadRef.current?.click();
      return;
    }

    if (item.action === 'navigate') {
      const nextLevel = item.nextLevel;
      const nextPath = [...(prev.path || []), item.label];
      setMentionPopover({ ...prev, level: nextLevel, path: nextPath, category: item.category, asset: item.asset, searchText: '' });
      mentionIndexRef.current = 0;
      setMenuLoading(true);

      try {
        let items = [];
        if (nextLevel === 'source') {
          items = [
            { id: 'character', icon: Drama, label: '角色工坊', action: 'navigate', nextLevel: 'assets', category: 'character' },
            { id: 'scene', icon: Mountain, label: '场景工坊', action: 'navigate', nextLevel: 'assets', category: 'scene' },
            { id: 'prop', icon: Wrench, label: '道具工坊', action: 'navigate', nextLevel: 'assets', category: 'prop' },
            { id: 'storyboard', icon: Clapperboard, label: '分镜', action: 'navigate', nextLevel: 'assets', category: 'storyboard' },
            { id: 'imageAgent', icon: Palette, label: '图片Agent', action: 'navigate', nextLevel: 'assets', category: 'imageAgent' },
          ];
        } else if (nextLevel === 'assets') {
          const cat = item.category;
          if (cat === 'character') {
            const data = await authenticatedFetch(`${API_HOST}/api/characters?projectId=${projectId}`);
            items = (data || []).filter(c => c.imageUrl).map(c => ({
              id: c.id, icon: null, label: c.name, imageUrl: c.imageUrl, url: c.imageUrl,
              displayName: c.name,
              sourceType: 'characters',
              sourceAssetId: c.id,
              guaVideoAssetId: c.guaVideoAssetId,
              guaVideoAssetStatus: c.guaVideoAssetStatus,
              engGuaVideoAssetId: c.engGuaVideoAssetId,
              engGuaVideoAssetStatus: c.engGuaVideoAssetStatus,
              guaOminiAssetId: c.guaOminiAssetId,
              guaOminiAssetStatus: c.guaOminiAssetStatus,
              dreaminaProxyAssetId: c.dreaminaProxyAssetId,
              dreaminaProxyAssetStatus: c.dreaminaProxyAssetStatus,
              voiceSampleUrl: c.voiceSampleUrl,
              voiceSampleName: c.voiceSampleName,
              voiceBoundAt: c.voiceBoundAt,
              action: (c.variations?.length > 0) ? 'navigate' : 'select',
              nextLevel: 'variants', category: cat, asset: c,
            }));
          } else if (cat === 'scene') {
            const data = await authenticatedFetch(`${API_HOST}/api/scenes?projectId=${projectId}`);
            items = (data || []).filter(s => s.imageUrl).map(s => ({
              id: s.id, icon: null, label: s.name, imageUrl: s.imageUrl, url: s.imageUrl,
              displayName: s.name,
              sourceType: 'scenes',
              sourceAssetId: s.id,
              guaVideoAssetId: s.guaVideoAssetId,
              guaVideoAssetStatus: s.guaVideoAssetStatus,
              engGuaVideoAssetId: s.engGuaVideoAssetId,
              engGuaVideoAssetStatus: s.engGuaVideoAssetStatus,
              guaOminiAssetId: s.guaOminiAssetId,
              guaOminiAssetStatus: s.guaOminiAssetStatus,
              dreaminaProxyAssetId: s.dreaminaProxyAssetId,
              dreaminaProxyAssetStatus: s.dreaminaProxyAssetStatus,
              action: (s.variations?.length > 0) ? 'navigate' : 'select',
              nextLevel: 'variants', category: cat, asset: s,
            }));
          } else if (cat === 'prop') {
            const data = await authenticatedFetch(`${API_HOST}/api/props?projectId=${projectId}`);
            items = (data || []).filter(p => p.imageUrl).map(p => ({
              id: p.id, icon: null, label: p.name, imageUrl: p.imageUrl, url: p.imageUrl,
              displayName: p.name,
              sourceType: 'props',
              sourceAssetId: p.id,
              guaVideoAssetId: p.guaVideoAssetId,
              guaVideoAssetStatus: p.guaVideoAssetStatus,
              engGuaVideoAssetId: p.engGuaVideoAssetId,
              engGuaVideoAssetStatus: p.engGuaVideoAssetStatus,
              guaOminiAssetId: p.guaOminiAssetId,
              guaOminiAssetStatus: p.guaOminiAssetStatus,
              dreaminaProxyAssetId: p.dreaminaProxyAssetId,
              dreaminaProxyAssetStatus: p.dreaminaProxyAssetStatus,
              action: (p.variations?.length > 0) ? 'navigate' : 'select',
              nextLevel: 'variants', category: cat, asset: p,
            }));
          } else if (cat === 'storyboard') {
            const data = await authenticatedFetch(`${API_HOST}/api/storyboard/load?episodeId=${episodeId}`);
            const shots = (data || []).filter(s => s.generated_image);
            items = shots.map((s, i) => ({
              id: `shot-${i}`, icon: null, label: `镜头${s.shot_number || i + 1}`, imageUrl: s.generated_image, url: s.generated_image,
              action: 'select',
            }));
          } else if (cat === 'imageAgent') {
            const data = await authenticatedFetch(`${API_HOST}/api/resource/query`, {
              method: 'POST', body: JSON.stringify({ projectId, type: 'IMAGE', page: 0, size: 50 }),
            });
            const list = data?.content || data || [];
            items = list.filter(r => r.url).map(r => ({
              id: r.id, icon: null, label: r.prompt || r.name || '图片', imageUrl: r.url, url: r.url,
              guaVideoAssetId: r.guaVideoAssetId,
              guaVideoAssetStatus: r.guaVideoAssetStatus,
              engGuaVideoAssetId: r.engGuaVideoAssetId,
              engGuaVideoAssetStatus: r.engGuaVideoAssetStatus,
              guaOminiAssetId: r.guaOminiAssetId,
              guaOminiAssetStatus: r.guaOminiAssetStatus,
              dreaminaProxyAssetId: r.dreaminaProxyAssetId,
              dreaminaProxyAssetStatus: r.dreaminaProxyAssetStatus,
              action: 'select',
            }));
          }
        } else if (nextLevel === 'variants') {
          const asset = item.asset;
          const variantCategory = item.category || prev.category || '';
          const assetName = asset.name || '主体';
          if (asset) {
            items = [{
              id: 'main', icon: null, label: assetName, imageUrl: asset.imageUrl, url: asset.imageUrl,
              displayName: assetName,
              action: 'select', category: variantCategory,
              sourceType: variantCategory === 'character' ? 'characters' : variantCategory === 'scene' ? 'scenes' : variantCategory === 'prop' ? 'props' : variantCategory,
              sourceAssetId: asset.id,
              guaVideoAssetId: asset.guaVideoAssetId,
              guaVideoAssetStatus: asset.guaVideoAssetStatus,
              engGuaVideoAssetId: asset.engGuaVideoAssetId,
              engGuaVideoAssetStatus: asset.engGuaVideoAssetStatus,
              guaOminiAssetId: asset.guaOminiAssetId,
              guaOminiAssetStatus: asset.guaOminiAssetStatus,
              dreaminaProxyAssetId: asset.dreaminaProxyAssetId,
              dreaminaProxyAssetStatus: asset.dreaminaProxyAssetStatus,
              voiceSampleUrl: asset.voiceSampleUrl,
              voiceSampleName: asset.voiceSampleName,
              voiceBoundAt: asset.voiceBoundAt,
            }];
            (asset.variations || []).forEach((v, i) => {
              if (v.imageUrl) {
                items.push({
                  id: `var-${i}`, icon: null, label: assetName, imageUrl: v.imageUrl, url: v.imageUrl,
                  displayName: assetName,
                  action: 'select', category: variantCategory,
                  sourceType: variantCategory === 'character' ? 'characters' : variantCategory === 'scene' ? 'scenes' : variantCategory === 'prop' ? 'props' : variantCategory,
                  sourceAssetId: asset.id,
                  guaVideoAssetId: v.guaVideoAssetId,
                  guaVideoAssetStatus: v.guaVideoAssetStatus,
                  engGuaVideoAssetId: v.engGuaVideoAssetId,
                  engGuaVideoAssetStatus: v.engGuaVideoAssetStatus,
                  guaOminiAssetId: v.guaOminiAssetId,
                  guaOminiAssetStatus: v.guaOminiAssetStatus,
                  dreaminaProxyAssetId: v.dreaminaProxyAssetId,
                  dreaminaProxyAssetStatus: v.dreaminaProxyAssetStatus,
                  voiceSampleUrl: asset.voiceSampleUrl,
                  voiceSampleName: asset.voiceSampleName,
                  voiceBoundAt: asset.voiceBoundAt,
                });
              }
            });
          }
        }
        setMenuItems(items);
      } catch (err) {
        console.error('加载菜单失败:', err);
        setMenuItems([]);
      } finally {
        setMenuLoading(false);
      }
    }
  }, [mentionPopover, projectId, episodeId]);

  // ── @ 多级菜单：选中资产图片（去重）──
  const handleMenuSelect = useCallback((item) => {
    if (!item.url) return;
    const category = mentionPopover?.category || item.category || '';
    if (selectedModel === WAND_VEGA_PROVIDER) {
      if (!isPublicWandMediaUrl(item.url) || getSeedance25ReferenceMediaType({ ...item, type: category }) !== 'image') {
        toast?.warning?.('Wand Vega 仅支持公网原始图片 URL；不能选择视频、音频或旧素材 ID');
        return;
      }
    }

    // select-existing：已有参考图，只插入 chip，不添加到 guaRefImages
    if (item.action === 'select-existing') {
      handleMentionSelect({ id: item.id, name: item.label, url: item.url, category });
      return;
    }

    // 新参考素材：去重添加 + 记录名字
    if (isGuaVideo && generationMode === 'ref2v') {
      if (category === 'video') {
        if (!guaRefVideos.includes(item.url)) {
          setGuaRefVideos(prev => [...prev, item.url]);
          setGuaRefNames(prev => new Map(prev).set(item.url, item.label));
        }
      } else if (!guaRefImages.includes(item.url)) {
        setGuaRefImages(prev => [...prev, item.url]);
        setGuaRefNames(prev => new Map(prev).set(item.url, item.label));
        rememberGuaRefMeta(item.url, item, category);
      } else {
        rememberGuaRefMeta(item.url, item, category);
      }
    } else if (isWanxVideo && generationMode === 'reference') {
      const mediaType = inferWanxVideoReferenceType(item, item.url);
      const added = addWanxMediaItem(createWanxVideoMediaItem({
        type: mediaType,
        url: item.url,
        name: item.label || getMediaNameFromUrl(item.url, '参考素材'),
        duration: item.duration,
      }));
      if (!added) return;
      handleMentionSelect({ id: item.id, name: item.label, url: item.url, category: mediaType });
      return;
    } else if (selectedModel === WAND_VEGA_PROVIDER && ['img2video', 'start-end'].includes(generationMode)) {
      setImages((current) => ({ ...current, [getWandFrameTarget(generationMode, current)]: item.url }));
    } else {
      const capabilities = getSeedance25Capabilities(selectedModel);
      if (capabilities && images.refs.length >= capabilities.maxReferenceImages) {
        toast?.warning?.(`${capabilities.label} 最多支持 ${capabilities.maxReferenceImages} 张参考图`);
        return;
      }
      if (!images.refs.some(r => r.url === item.url)) {
        setImages(prev => ({ ...prev, refs: [...prev.refs, { id: Date.now(), url: item.url, name: item.label, type: category }] }));
      }
    }
    // 插入 chip 到 editor
    handleMentionSelect({ id: item.id, name: item.label, url: item.url, category });
  }, [addWanxMediaItem, isGuaVideo, isWanxVideo, selectedModel, generationMode, guaRefImages, guaRefVideos, images.refs, handleMentionSelect, mentionPopover, rememberGuaRefMeta, toast]);

  // ── @ 多级菜单：本地上传处理（去重）──
  const handleMentionUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (selectedModel === WAND_VEGA_PROVIDER && !String(file.type || '').startsWith('image/')) {
      toast?.warning?.('Wand Vega 当前入口仅支持图片素材');
      e.target.value = '';
      return;
    }
    const uploadContext = draftContextRef.current;
    try {
      const metadata = isWanxVideo && generationMode === 'reference'
        ? await validateWanxVideoMediaFile(file, WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE)
        : null;
      if (uploadContext !== draftContextRef.current) return;
      const url = isWanxVideo ? await uploadFileMultipart(file) : await uploadFileToCos(file);
      if (uploadContext !== draftContextRef.current) return;
      const name = file.name.split('.')[0] || '本地图片';
      // 去重
      if (isGuaVideo && generationMode === 'ref2v') {
        let appendResult = { added: false, error: '' };
        flushSync(() => {
          setGuaRefImages(prev => {
            if (prev.includes(url)) return prev;
            appendResult = { added: true, error: '' };
            return [...prev, url];
          });
        });
        if (appendResult.error) {
          toast?.warning?.(appendResult.error);
          return;
        }
        if (!appendResult.added) return;
        setGuaRefNames(prev => new Map(prev).set(url, name));
      } else if (isWanxVideo && generationMode === 'reference') {
        const added = addWanxMediaItem(createWanxVideoMediaItem({
          type: WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE,
          url,
          name,
          ...metadata,
        }));
        if (!added) return;
      } else if (selectedModel === WAND_VEGA_PROVIDER && ['img2video', 'start-end'].includes(generationMode)) {
        setImages((current) => ({ ...current, [getWandFrameTarget(generationMode, current)]: url }));
      } else {
        const capabilities = getSeedance25Capabilities(selectedModel);
        if (capabilities && images.refs.length >= capabilities.maxReferenceImages) {
          toast?.warning?.(`${capabilities.label} 最多支持 ${capabilities.maxReferenceImages} 张参考图`);
          return;
        }
        if (!images.refs.some(r => r.url === url)) {
          setImages(prev => ({ ...prev, refs: [...prev.refs, { id: Date.now(), url, name }] }));
        }
      }
      const category = isWanxVideo && generationMode === 'reference'
        ? WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE
        : (isGuaVideo && generationMode === 'ref2v') ? 'image' : 'upload';
      handleMentionSelect({ id: Date.now(), name, url, category });
    } catch (err) {
      toast.error('上传失败: ' + err.message);
    } finally {
      e.target.value = '';
    }
  }, [addWanxMediaItem, isGuaVideo, isWanxVideo, selectedModel, generationMode, guaRefImages, images.refs, handleMentionSelect, toast]);

  const handleMentionVideoUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const uploadContext = draftContextRef.current;
    if (!isWanxVideo && file.size > 50 * 1024 * 1024) {
      toast?.warning('视频不能超过50MB');
      e.target.value = '';
      return;
    }
    try {
      const metadata = isWanxVideo && generationMode === 'reference'
        ? await validateWanxVideoMediaFile(file, WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO)
        : null;
      if (uploadContext !== draftContextRef.current) return;
      const url = isWanxVideo ? await uploadFileMultipart(file) : await uploadFileToCos(file);
      if (uploadContext !== draftContextRef.current) return;
      const name = file.name.split('.')[0] || '本地视频';
      if (isWanxVideo && generationMode === 'reference') {
        const added = addWanxMediaItem(createWanxVideoMediaItem({
          type: WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO,
          url,
          name,
          ...metadata,
        }));
        if (!added) return;
      } else if (supportsSeedance25MediaReferences(selectedModel) && generationMode === 'reference') {
        setImages(prev => ({ ...prev, refs: [...prev.refs, { id: Date.now(), url, name, mediaType: 'video' }] }));
      } else {
        if (!guaRefVideos.includes(url)) setGuaRefVideos(prev => [...prev, url]);
        setGuaRefNames(prev => new Map(prev).set(url, name));
      }
      handleMentionSelect({
        id: Date.now(),
        name,
        url,
        category: isWanxVideo ? WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO : 'video',
      });
    } catch (err) {
      toast.error('上传失败: ' + err.message);
    } finally {
      e.target.value = '';
    }
  }, [addWanxMediaItem, generationMode, guaRefVideos, handleMentionSelect, isWanxVideo, selectedModel, toast]);

  // ── @ 多级菜单：返回上一级 ──
  // 往 editor 末尾追加 chip（非 @ 方式添加参考图时调用）
  const appendChipToEditor = useCallback((name, url, category = '') => {
    const el = editorRef.current;
    if (!el) return;
    const chip = createMentionChip(name, url, mc.color || theme.colors.accentPrimary, category);
    const lastChild = el.lastChild;
    if (!lastChild || (lastChild.nodeType === Node.TEXT_NODE && !lastChild.textContent.endsWith(' ')) || lastChild.nodeType !== Node.TEXT_NODE) {
      el.appendChild(document.createTextNode(' '));
    }
    el.appendChild(chip);
    el.appendChild(document.createTextNode('\u00A0'));
    setPrompt(getPromptWithMarkers());
  }, [mc.color, theme.colors.accentPrimary, getPromptWithMarkers]);

  const handleWanxMediaUpload = useCallback(async (event, mediaType) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const uploadContext = draftContextRef.current;
    setWanxUploading(true);
    try {
      const metadata = await validateWanxVideoMediaFile(file, mediaType);
      if (uploadContext !== draftContextRef.current) return;
      const url = await uploadFileMultipart(file);
      if (uploadContext !== draftContextRef.current) return;
      const item = createWanxVideoMediaItem({
        type: mediaType,
        url,
        name: file.name,
        ...metadata,
      });
      if (addWanxMediaItem(item) && [
        WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE,
        WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO,
        WANX_VIDEO_MEDIA_TYPES.REFERENCE_AUDIO,
      ].includes(mediaType)) {
        appendChipToEditor(file.name, url, mediaType);
      }
    } catch (uploadError) {
      toast?.error(`上传失败: ${uploadError.message}`);
    } finally {
      setWanxUploading(false);
      event.target.value = '';
    }
  }, [addWanxMediaItem, appendChipToEditor, toast]);

  const handleWanxAddLink = useCallback(() => {
    const url = String(wanxLinkDraft || '').trim();
    let parsedUrl;
    try {
      parsedUrl = new URL(url);
    } catch {
      parsedUrl = null;
    }
    if (!parsedUrl || !['http:', 'https:'].includes(parsedUrl.protocol)) {
      toast?.warning('请输入公开可访问的 HTTP/HTTPS 网页链接');
      return;
    }
    const added = addWanxMediaItem(createWanxVideoMediaItem({
      type: WANX_VIDEO_MEDIA_TYPES.LINK,
      url,
      name: url,
    }));
    if (added) {
      setWanxLinkDraft('');
      setShowWanxLinkInput(false);
    }
  }, [addWanxMediaItem, toast, wanxLinkDraft]);

  const handleMenuBack = useCallback(() => {
    if (!mentionPopover || !mentionPopover.path?.length) return;
    const newPath = [...mentionPopover.path];
    newPath.pop();
    if (newPath.length === 0) {
      setMentionPopover({ ...mentionPopover, level: 'root', path: [], searchText: '' });
      setMenuItems([]);
    } else {
      // 回到上一级需要重新触发导航，简化处理直接回 source
      setMentionPopover({ ...mentionPopover, level: 'source', path: ['资源管理器'], searchText: '' });
      setMenuItems([
        { id: 'character', icon: Drama, label: '角色工坊', action: 'navigate', nextLevel: 'assets', category: 'character' },
        { id: 'scene', icon: Mountain, label: '场景工坊', action: 'navigate', nextLevel: 'assets', category: 'scene' },
        { id: 'prop', icon: Wrench, label: '道具工坊', action: 'navigate', nextLevel: 'assets', category: 'prop' },
        { id: 'storyboard', icon: Clapperboard, label: '分镜', action: 'navigate', nextLevel: 'assets', category: 'storyboard' },
        { id: 'imageAgent', icon: Palette, label: '图片Agent', action: 'navigate', nextLevel: 'assets', category: 'imageAgent' },
      ]);
    }
    mentionIndexRef.current = 0;
  }, [mentionPopover]);

  // ── @ 提及：键盘交互 ──
  const handleEditorKeyDown = useCallback((e) => {
    if (mentionPopover && visibleMentions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        updateMentionIndex((mentionIndexRef.current + 1) % visibleMentions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        updateMentionIndex((mentionIndexRef.current - 1 + visibleMentions.length) % visibleMentions.length);
        return;
      }
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        const item = visibleMentions[Math.min(mentionIndexRef.current, visibleMentions.length - 1)];
        if (item?.action === 'select' || item?.action === 'select-existing') {
          handleMenuSelect(item);
        } else if (item?.action === 'navigate' || item?.action === 'upload' || item?.action === 'upload-image' || item?.action === 'upload-video') {
          handleMenuNavigate(item);
        } else {
          handleMentionSelect(item);
        }
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (mentionPopover.path?.length > 0) {
          handleMenuBack();
        } else {
          setMentionPopover(null);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionPopover(null);
        return;
      }
    }
    // Backspace 删除 mention chip（不影响参考图列表）
    if (e.key === 'Backspace') {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && sel.isCollapsed) {
        const range = sel.getRangeAt(0);
        const node = range.startContainer;
        // 光标在文本节点开头，前一个兄弟是 chip
        if (node.nodeType === Node.TEXT_NODE && range.startOffset === 0) {
          const prev = node.previousSibling;
          if (prev?.dataset?.mention) {
            e.preventDefault();
            prev.remove();
            setPrompt(getPromptWithMarkers());
            return;
          }
        }
        // 光标在元素节点中，前一个子节点是 chip
        if (node === editorRef.current) {
          const child = node.childNodes[range.startOffset - 1];
          if (child?.dataset?.mention) {
            e.preventDefault();
            child.remove();
            setPrompt(getPromptWithMarkers());
            return;
          }
        }
      }
    }
  }, [mentionPopover, visibleMentions, updateMentionIndex, handleMentionSelect, getPromptWithMarkers]);

  // 加载 GuaVideo 用量数据
  const loadUsageData = useCallback(async (date) => {
    setUsageLoading(true);
    try {
      const secret = sessionStorage.getItem('admin_secret') || '';
      const res = await authenticatedFetch(`${API_HOST}/api/admin/guavideo-usage?secret=${encodeURIComponent(secret)}&date=${date}`);
      setUsageData(res);
    } catch (e) {
      console.error('加载用量失败:', e);
    }
    setUsageLoading(false);
  }, []);

  // ── JSX ──
  return (
    <div className="studio-generation h-full flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      {/* 全局 hover 样式 */}
      <style>{`
        .va-card { border-radius: var(--workspace-radius-md); box-shadow: none; }
        .va-editor[data-placeholder]:empty:before { content: attr(data-placeholder); color: var(--color-text-muted); pointer-events: none; }
        .va-editor:focus { outline: none; }
        .va-editor [data-mention] { user-select: all; }
        .va-dot { width: 6px; height: 6px; border-radius: 9999px; display: inline-block; flex-shrink: 0; }
      `}</style>

      {/* ═══ 中间：视频卡片流 ═══ */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto custom-scrollbar px-5 py-4">

        {/* 李育东专属：GuaVideo 用量入口 */}
        {isAdmin && (
          <div className="mb-4 max-w-4xl mx-auto">
            <button
              onClick={() => { setShowUsagePanel(!showUsagePanel); if (!showUsagePanel) loadUsageData(usageDate); }}
              className="va-btn flex items-center gap-2 px-3 py-1.5 rounded-md text-2xs font-semibold"
              style={{ backgroundColor: 'var(--color-red-light)', color: theme.colors.error, border: '1px solid var(--color-border-primary)' }}
            >
              GuaVideo 用量监控
            </button>


            {usagePanelMount.mounted && (
              <div className={`mt-3 p-4 production-panel space-y-4 ${usagePanelMount.exiting ? 'animate-out fade-out slide-out-to-top-2 duration-150' : 'animate-in fade-in slide-in-from-top-2 duration-200'}`} style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                {/* 日期选择 */}
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold" style={{ color: 'var(--color-text-primary)' }}>GuaVideo 用量</span>
                  <input type="date" value={usageDate}
                    onChange={(e) => { setUsageDate(e.target.value); loadUsageData(e.target.value); }}
                    className="px-2 py-1 rounded-lg text-xs outline-none"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                  />
                  <button onClick={() => loadUsageData(usageDate)} className="va-btn px-2 py-1 rounded-lg text-2xs" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}>
                    刷新
                  </button>
                </div>

                {usageLoading ? (
                  <div className="text-center py-4"><Loader2 size={20} className="animate-spin mx-auto" style={{ color: 'var(--color-text-muted)' }} /></div>
                ) : usageData ? (
                  <>
                    {/* 汇总 */}
                    <div className="flex gap-4">
                      <div className="flex-1 p-3 rounded-xl text-center" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                        <div className="text-xl font-semibold" style={{ color: theme.colors.accentPrimary }}>{usageData.totalRequests || 0}</div>
                        <div className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>总请求数</div>
                      </div>
                      <div className="flex-1 p-3 rounded-xl text-center" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                        <div className="text-xl font-semibold" style={{ color: theme.colors.error }}>{usageData.totalDurationSeconds || 0}s</div>
                        <div className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>总时长</div>
                      </div>
                    </div>

                    {/* 按模型 */}
                    {usageData.byModel && Object.keys(usageData.byModel).length > 0 && (
                      <div>
                        <div className="text-2xs font-bold mb-2" style={{ color: 'var(--color-text-muted)' }}>按模型</div>
                        <div className="flex gap-2 flex-wrap">
                          {Object.entries(usageData.byModel).map(([model, stats]) => (
                            <div key={model} className="px-3 py-2 rounded-lg text-xs" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                              <span className="font-bold" style={{ color: 'var(--color-text-primary)' }}>{getPublicVideoModelName(model)}</span>
                              <span className="ml-2" style={{ color: 'var(--color-text-muted)' }}>{stats.requests}次 · {stats.totalDuration}s</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 按用户 */}
                    {usageData.byUser && usageData.byUser.length > 0 && (
                      <div>
                        <div className="text-2xs font-bold mb-2" style={{ color: 'var(--color-text-muted)' }}>按用户</div>
                        <div className="space-y-2">
                          {usageData.byUser.map((user, ui) => (
                            <div key={ui} className="p-3 rounded-xl" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold" style={{ color: 'var(--color-text-primary)' }}>{user.userId}</span>
                                <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>{user.requests}次 · {user.totalDuration}s</span>
                              </div>
                              <div className="space-y-1">
                                {(user.details || []).map((d, di) => (
                                  <div key={di} className="flex items-center gap-2 text-2xs" style={{ color: 'var(--color-text-secondary)' }}>
                                    <span style={{ color: 'var(--color-text-muted)' }}>{d.time}</span>
                                    <span className="px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-secondary)', color: theme.colors.accentPrimary }}>{getPublicVideoModelName(d.model)}</span>
                                    <span>{d.mode}</span>
                                    <span>{d.duration}s</span>
                                    <span className="truncate flex-1 opacity-60">{d.prompt}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {usageData.totalRequests === 0 && (
                      <div className="text-center py-4 text-xs" style={{ color: 'var(--color-text-muted)' }}>当天无 {mc.name} 使用记录</div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-4 text-xs" style={{ color: 'var(--color-text-muted)' }}>点击刷新加载数据</div>
                )}
              </div>
            )}
          </div>
        )}


        {mode === 'generate' ? (
          <>
            {videos.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center gap-4">
                <div className="w-14 h-14 rounded-md flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)' }}>
                  <Film size={28} style={{ color: 'var(--color-text-muted)', opacity: 0.4 }} />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>还没有视频</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>在下方输入提示词开始创作</p>
                </div>
              </div>
            ) : (
              <div className="space-y-6 max-w-4xl mx-auto">
                {groupedVideos.map((group, gi) => (
                  <div key={gi}>
                    <div className="mb-4">
                      <span className="text-2xs font-semibold px-3 py-1 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>{group.label}</span>
                    </div>
                    <div className="space-y-4">
                      {group.videos.map(video => (
                        <VideoCard
                          key={video.id}
                          video={video}
                          theme={theme}
                          onRetry={handleRetry}
                          onDelete={handleDelete}
                          onCancel={handleCancel}
                          onEraseSubtitle={handleEraseSubtitle}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          /* 后处理模式卡片流 */
          ppTasks.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center gap-4">
              <div className="w-14 h-14 rounded-md flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)' }}>
                <Film size={28} style={{ color: 'var(--color-text-muted)', opacity: 0.4 }} />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                  还没有去字幕记录
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>选择视频开始处理</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 max-w-4xl mx-auto">
              {ppTasks.filter(t => t.type === 'erase-subtitle').map(task => (
                <PostProcessCard key={task.id} task={task} theme={theme} onDelete={handlePpDelete} onRetry={handlePpRetry} />
              ))}
            </div>
          )
        )}
      </div>

      {/* ═══ 底部输入区 ═══ */}
      <div className="generation-input-dock shrink-0 px-5 pb-4 pt-2" style={{ backgroundColor: 'var(--color-bg-primary)', borderTop: '1px solid var(--color-border-secondary)' }}>
        {/* 错误提示 */}
        {error && (
          <div className="mb-2 p-2 rounded-lg flex items-center gap-2 text-xs" style={{ backgroundColor: 'var(--color-red-light)', color: 'var(--color-error)' }}>
            <AlertCircle size={14} /> {error}
            <button onClick={() => setError(null)} className="ml-auto"><X size={12} /></button>
          </div>
        )}

        {/* 参考素材预览条 — 在输入框上方（仅生成模式） */}
        {mode === 'generate' && generationMode !== 'text2video' && (
          <div className="mb-2 flex items-center gap-2 flex-wrap">
            {/* 万相 3.0 首帧/首尾帧 */}
            {isWanxVideo && (generationMode === 'img2video' || generationMode === 'start-end') && (
              <>
                {[wanxFirstFrame, generationMode === 'start-end' ? wanxLastFrame : null].filter(Boolean).map(item => (
                  <div key={item.id} className="relative group w-14 h-14 rounded-md shrink-0"
                    style={{ border: '1px solid var(--color-border-primary)' }}
                    onMouseEnter={(event) => { const rect = event.currentTarget.getBoundingClientRect(); setHoverPreview({ url: item.url, x: rect.left + rect.width / 2, y: rect.top }); }}
                    onMouseLeave={() => setHoverPreview(null)}
                  >
                    <img src={item.url} className="w-full h-full object-cover rounded-md" alt="" />
                    <button onClick={() => { setHoverPreview(null); removeWanxMediaItem(item.id); }} className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center generation-reference-remove transition-opacity" style={{ backgroundColor: theme.colors.error, color: 'white' }}><X size={8} /></button>
                    <span className="absolute bottom-0 left-0 right-0 text-center text-2xs font-bold py-px rounded-b-md" style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}>{getWanxVideoMediaLabel(item, wanxMedia)}</span>
                  </div>
                ))}
                {(generationMode === 'img2video' ? !wanxFirstFrame : (!wanxFirstFrame || !wanxLastFrame)) && (
                  <div className="relative shrink-0" ref={frameMenuRef}>
                    <button
                      onClick={() => {
                        const target = generationMode === 'img2video' ? 'start' : (!wanxFirstFrame ? 'start' : 'end');
                        setFrameTarget(target);
                        setShowFrameMenu(!showFrameMenu);
                      }}
                      className="va-btn w-14 h-14 rounded-md flex flex-col items-center justify-center gap-1 shrink-0 generation-feedback"
                      style={{ border: `1px dashed ${mc.color}50`, color: mc.color }}
                    >
                      <Plus size={16} />
                      <span className="text-2xs font-medium">{generationMode === 'img2video' ? '首帧' : (!wanxFirstFrame ? '首帧' : '尾帧')}</span>
                    </button>
                    {frameMenuMount.mounted && ReactDOM.createPortal(
                      <div className="fixed inset-0 z-[300]" onMouseDown={() => setShowFrameMenu(false)}>
                        <div
                          className={`absolute w-52 rounded-lg shadow-2xl overflow-hidden ${frameMenuMount.exiting ? 'animate-out fade-out slide-out-to-bottom-2 duration-150' : 'animate-in fade-in slide-in-from-bottom-2 duration-150'}`}
                          style={{
                            bottom: window.innerHeight - (frameMenuRef.current?.getBoundingClientRect().top || 0) + 8,
                            left: frameMenuRef.current?.getBoundingClientRect().left || 16,
                            backgroundColor: 'var(--color-bg-secondary)',
                            border: '1px solid var(--color-border-primary)',
                          }}
                          onClick={event => event.stopPropagation()}
                          onMouseDown={event => event.stopPropagation()}
                        >
                          <button onClick={(event) => { event.stopPropagation(); wanxFrameInputRef.current?.click(); setShowFrameMenu(false); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs transition-colors hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-primary)' }}><Upload size={14} style={{ color: mc.color }} /> 本地上传</button>
                          <button onClick={(event) => { event.stopPropagation(); setShowFrameResourceSelector(true); setShowFrameMenu(false); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs transition-colors hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-primary)' }}><FolderOpen size={14} style={{ color: mc.color }} /> 从资源库选择</button>
                        </div>
                      </div>,
                      document.body
                    )}
                  </div>
                )}
                <input
                  ref={wanxFrameInputRef}
                  type="file"
                  accept={WANX_VIDEO_ACCEPT.image}
                  className="hidden"
                  onChange={event => handleWanxMediaUpload(
                    event,
                    frameTarget === 'end' ? WANX_VIDEO_MEDIA_TYPES.LAST_FRAME : WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME,
                  )}
                />
              </>
            )}

            {/* 万相 3.0 参考生视频：图/视频/音频分别编号，文件与链接二选一。 */}
            {isWanxVideo && generationMode === 'reference' && (
              <>
                {wanxReferenceMedia.map(item => {
                  const label = getWanxVideoMediaLabel(item, wanxMedia);
                  const isImage = item.type === WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE;
                  const MediaIcon = item.type === WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO
                    ? Video
                    : item.type === WANX_VIDEO_MEDIA_TYPES.REFERENCE_AUDIO
                      ? Music
                      : item.type === WANX_VIDEO_MEDIA_TYPES.FILE ? FileText : Link2;
                  return isImage ? (
                    <div key={item.id} className="relative group w-14 h-14 rounded-md shrink-0"
                      style={{ border: '1px solid var(--color-border-primary)' }}
                      onMouseEnter={(event) => { const rect = event.currentTarget.getBoundingClientRect(); setHoverPreview({ url: item.url, x: rect.left + rect.width / 2, y: rect.top }); }}
                      onMouseLeave={() => setHoverPreview(null)}
                    >
                      <img src={item.url} className="w-full h-full object-cover rounded-md" alt="" />
                      <span className="absolute bottom-0 left-0 right-0 text-center text-2xs font-bold py-px rounded-b-md" style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}>{label}</span>
                      <button onClick={() => { setHoverPreview(null); removeWanxMediaItem(item.id); }} className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center generation-reference-remove transition-opacity" style={{ backgroundColor: theme.colors.error, color: 'white' }}><X size={8} /></button>
                    </div>
                  ) : (
                    <div key={item.id} className="relative group flex items-center gap-1.5 px-2 py-1.5 rounded-md max-w-[180px]" style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)' }}>
                      <MediaIcon size={12} style={{ color: mc.color }} />
                      <span className="text-2xs font-bold shrink-0" style={{ color: mc.color }}>{label}</span>
                      <span className="text-2xs truncate" style={{ color: 'var(--color-text-secondary)' }}>{item.name || getMediaNameFromUrl(item.url, label)}</span>
                      <button onClick={() => removeWanxMediaItem(item.id)} className="generation-reference-remove transition-opacity"><X size={10} style={{ color: theme.colors.error }} /></button>
                    </div>
                  );
                })}
                <div className="relative shrink-0" ref={addMenuRef}>
                  <button
                    onClick={() => setShowAddMenu(!showAddMenu)}
                    disabled={wanxUploading}
                    className="va-btn w-14 h-14 rounded-md flex flex-col items-center justify-center gap-1 shrink-0 generation-feedback disabled:opacity-50"
                    style={{ border: `1px dashed ${mc.color}50`, color: mc.color }}
                  >
                    {wanxUploading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                    <span className="text-2xs font-medium">{wanxUploading ? '上传中' : '添加'}</span>
                  </button>
                  {addMenuMount.mounted && ReactDOM.createPortal(
                    <div className="fixed inset-0 z-[300]" onMouseDown={() => setShowAddMenu(false)}>
                      <div
                        className={`absolute w-56 rounded-lg shadow-2xl overflow-hidden ${addMenuMount.exiting ? 'animate-out fade-out slide-out-to-bottom-2 duration-150' : 'animate-in fade-in slide-in-from-bottom-2 duration-150'}`}
                        style={{
                          bottom: window.innerHeight - (addMenuRef.current?.getBoundingClientRect().top || 0) + 8,
                          left: addMenuRef.current?.getBoundingClientRect().left || 16,
                          backgroundColor: 'color-mix(in srgb, var(--color-bg-secondary) 88%, transparent)',
                          backdropFilter: 'blur(20px) saturate(1.5)',
                          WebkitBackdropFilter: 'blur(20px) saturate(1.5)',
                          border: '1px solid var(--color-border-primary)',
                        }}
                        onClick={event => event.stopPropagation()}
                        onMouseDown={event => event.stopPropagation()}
                      >
                        <button onClick={() => { wanxImageInputRef.current?.click(); setShowAddMenu(false); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-primary)' }}><ImageIcon size={14} style={{ color: mc.color }} /> 上传参考图（最多10张）</button>
                        <button onClick={() => { setShowResourceSelector(true); setShowAddMenu(false); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-primary)' }}><FolderOpen size={14} style={{ color: mc.color }} /> 从资源库选择</button>
                        <button onClick={() => { wanxVideoInputRef.current?.click(); setShowAddMenu(false); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-primary)' }}><Video size={14} style={{ color: mc.color }} /> 上传参考视频（最多5段）</button>
                        <button onClick={() => { wanxAudioInputRef.current?.click(); setShowAddMenu(false); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-primary)' }}><Music size={14} style={{ color: mc.color }} /> 上传参考音频（最多5段）</button>
                        {!wanxHasFileOrLink && (
                          <>
                            <button onClick={() => { wanxFileInputRef.current?.click(); setShowAddMenu(false); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-primary)' }}><FileText size={14} style={{ color: mc.color }} /> 上传参考文件</button>
                            <button onClick={() => { setShowWanxLinkInput(true); setShowAddMenu(false); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-primary)' }}><Link2 size={14} style={{ color: mc.color }} /> 添加网页链接</button>
                          </>
                        )}
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
                {showWanxLinkInput && (
                  <div className="flex items-center gap-1 p-1 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)' }}>
                    <input
                      value={wanxLinkDraft}
                      onChange={event => setWanxLinkDraft(event.target.value)}
                      onKeyDown={event => { if (event.key === 'Enter') handleWanxAddLink(); }}
                      placeholder="https://公开网页地址"
                      className="w-56 bg-transparent px-2 text-xs outline-none"
                      style={{ color: 'var(--color-text-primary)' }}
                    />
                    <button onClick={handleWanxAddLink} className="px-2 py-1 rounded text-2xs font-bold" style={{ color: 'white', backgroundColor: mc.color }}>添加</button>
                    <button onClick={() => { setShowWanxLinkInput(false); setWanxLinkDraft(''); }} className="p-1"><X size={11} style={{ color: 'var(--color-text-muted)' }} /></button>
                  </div>
                )}
              </>
            )}
            {/* GuaVideo 图生/首尾帧，以及 Wand Vega 首尾帧，共用原图上传控件。 */}
            {((isGuaVideo || isWandVegaVideo)
              && (generationMode === 'img2video' || generationMode === 'start-end')) && (
              <>
                {images.start && (
                  <div className="relative group w-14 h-14 rounded-md shrink-0"
                    style={{ border: '1px solid var(--color-border-primary)' }}
                    onMouseEnter={(e) => { const r = e.currentTarget.getBoundingClientRect(); setHoverPreview({ url: images.start, x: r.left + r.width / 2, y: r.top }); }}
                    onMouseLeave={() => setHoverPreview(null)}
                  >
                    <img src={images.start} className="w-full h-full object-cover rounded-md" alt="" />
                    <button onClick={() => { setHoverPreview(null); removeImage('start'); }} className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center generation-reference-remove transition-opacity" style={{ backgroundColor: theme.colors.error, color: 'white' }}><X size={8} /></button>
                    <span className="absolute bottom-0 left-0 right-0 text-center text-2xs font-bold py-px rounded-b-md" style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}>首帧</span>
                  </div>
                )}
                {generationMode === 'start-end' && images.end && (
                  <div className="relative group w-14 h-14 rounded-md shrink-0"
                    style={{ border: '1px solid var(--color-border-primary)' }}
                    onMouseEnter={(e) => { const r = e.currentTarget.getBoundingClientRect(); setHoverPreview({ url: images.end, x: r.left + r.width / 2, y: r.top }); }}
                    onMouseLeave={() => setHoverPreview(null)}
                  >
                    <img src={images.end} className="w-full h-full object-cover rounded-md" alt="" />
                    <button onClick={() => { setHoverPreview(null); removeImage('end'); }} className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center generation-reference-remove transition-opacity" style={{ backgroundColor: theme.colors.error, color: 'white' }}><X size={8} /></button>
                    <span className="absolute bottom-0 left-0 right-0 text-center text-2xs font-bold py-px rounded-b-md" style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}>尾帧</span>
                  </div>
                )}
                {/* 添加按钮：img2video 只需首帧，start-end 需首帧+尾帧 */}
                {(generationMode === 'img2video' ? !images.start : (!images.start || !images.end)) && (
                  <div className="relative shrink-0" ref={frameMenuRef}>
                    <button
                      onClick={() => {
                        const target = generationMode === 'img2video' ? 'start' : (!images.start ? 'start' : 'end');
                        setFrameTarget(target);
                        setShowFrameMenu(!showFrameMenu);
                      }}
                      className="va-btn w-14 h-14 rounded-md flex flex-col items-center justify-center gap-1 shrink-0 generation-feedback"
                      style={{ border: `1px dashed ${mc.color || theme.colors.accentPrimary}50`, color: mc.color || theme.colors.accentPrimary }}
                    >
                      <Plus size={16} />
                      <span className="text-2xs font-medium">{generationMode === 'img2video' ? '首帧' : (!images.start ? '首帧' : '尾帧')}</span>
                    </button>
                    {frameMenuMount.mounted && ReactDOM.createPortal(
                      <div className="fixed inset-0 z-[300]" onMouseDown={() => setShowFrameMenu(false)}>
                        <div
                          className={`absolute w-52 rounded-lg shadow-2xl overflow-hidden ${frameMenuMount.exiting ? 'animate-out fade-out slide-out-to-bottom-2 duration-150' : 'animate-in fade-in slide-in-from-bottom-2 duration-150'}`}
                          style={{
                            bottom: window.innerHeight - (frameMenuRef.current?.getBoundingClientRect().top || 0) + 8,
                            left: frameMenuRef.current?.getBoundingClientRect().left || 16,
                            backgroundColor: 'var(--color-bg-secondary)',
                            border: '1px solid var(--color-border-primary)',
                          }}
                          onClick={e => e.stopPropagation()}
                          onMouseDown={e => e.stopPropagation()}
                        >
                          <button onClick={(e) => { e.stopPropagation(); imageInputRef.current?.click(); setShowFrameMenu(false); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs transition-colors hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-primary)' }}><Upload size={14} style={{ color: mc.color || theme.colors.accentPrimary }} /> 本地上传</button>
                          <button onClick={(e) => { e.stopPropagation(); setShowFrameResourceSelector(true); setShowFrameMenu(false); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs transition-colors hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-primary)' }}><FolderOpen size={14} style={{ color: mc.color || theme.colors.accentPrimary }} /> 从资源库选择</button>
                        </div>
                      </div>,
                      document.body
                    )}
                  </div>
                )}
                {/* 首帧/尾帧本地上传隐藏 input */}
                <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={e => { handleImageUpload(frameTarget, e.target.files[0], e.target); }} />
              </>
            )}
            {/* GuaVideo 参考素材（ref2v / edit-video / extend-video） */}
            {isGuaVideo && generationMode !== 'img2video' && generationMode !== 'start-end' && (
              <>
                {guaRefImages.map((url, i) => (
                  <div key={`img-${i}`} className="relative group w-14 h-14 rounded-md shrink-0"
                    style={{ border: statusMap[url] === "Active" ? '2px solid #22c55e' : '1px solid var(--color-border-primary)' }}
                    onMouseEnter={(e) => { const r = e.currentTarget.getBoundingClientRect(); setHoverPreview({ url, x: r.left + r.width / 2, y: r.top }); }}
                    onMouseLeave={() => setHoverPreview(null)}
                  >
                    <img src={url} className="w-full h-full object-cover rounded-md" alt="" />
                    <div className="absolute bottom-0 left-0 right-0 text-center text-2xs font-bold py-px truncate px-0.5 rounded-b-md" style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}>{guaRefNames.get(url) || `图片${i + 1}`}</div>
                    {statusMap[url] === "Active" && (
                      <div className="absolute top-0.5 left-0.5 w-3.5 h-3.5 rounded-md flex items-center justify-center" style={{ backgroundColor: 'color-mix(in srgb, var(--color-success) 85%, transparent)' }}>
                        <Check size={8} color="white" strokeWidth={3} />
                      </div>
                    )}
                    <button onClick={() => { draftContextRef.current = { ...draftContextRef.current }; setHoverPreview(null); const delUrl = guaRefImages[i]; if (!guaRefImages.some((url, j) => j !== i && url === delUrl)) removeReferenceMention(delUrl); setGuaRefImages(prev => prev.filter((_, j) => j !== i)); setGuaRefNames(prev => { const m = new Map(prev); m.delete(delUrl); return m; }); removeGuaRefMeta(delUrl); }} className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center generation-reference-remove generation-feedback z-10" style={{ backgroundColor: theme.colors.error, color: 'white' }}><X size={8} /></button>
                  </div>
                ))}
                {guaRefVideos.map((url, i) => (
                  <div key={`vid-${i}`} className="relative group flex items-center gap-1 px-2 py-1 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)' }}>
                    <Video size={11} style={{ color: 'var(--color-text-muted)' }} /><span className="text-2xs truncate max-w-[60px]" style={{ color: 'var(--color-text-secondary)' }}>{guaRefNames.get(url) || getMediaNameFromUrl(url, `视频${i + 1}`)}</span>
                    <button onClick={() => { draftContextRef.current = { ...draftContextRef.current }; setHoverPreview(null); const delUrl = guaRefVideos[i]; if (!guaRefVideos.some((url, j) => j !== i && url === delUrl)) removeReferenceMention(delUrl); setGuaRefVideos(prev => prev.filter((_, j) => j !== i)); setGuaRefNames(prev => { const m = new Map(prev); m.delete(delUrl); return m; }); }} className="generation-reference-remove transition-opacity"><X size={10} style={{ color: theme.colors.error }} /></button>
                  </div>
                ))}
                {/* 添加参考图按钮 */}
                <div className="relative shrink-0" ref={addMenuRef}>
                  <button
                    onClick={() => setShowAddMenu(!showAddMenu)}
                    className="va-btn w-14 h-14 rounded-md flex flex-col items-center justify-center gap-1 shrink-0 generation-feedback"
                    style={{ border: `1px dashed ${mc.color || theme.colors.accentPrimary}50`, color: mc.color || theme.colors.accentPrimary }}
                  >
                    <Plus size={16} />
                    <span className="text-2xs font-medium">添加</span>
                  </button>
                  {addMenuMount.mounted && ReactDOM.createPortal(
                    <div className="fixed inset-0 z-[300]" onMouseDown={() => setShowAddMenu(false)}>
                      <div
                        className={`absolute w-52 rounded-lg shadow-2xl overflow-hidden ${addMenuMount.exiting ? 'animate-out fade-out slide-out-to-bottom-2 duration-150' : 'animate-in fade-in slide-in-from-bottom-2 duration-150'}`}
                        style={{
                          bottom: window.innerHeight - (addMenuRef.current?.getBoundingClientRect().top || 0) + 8,
                          left: addMenuRef.current?.getBoundingClientRect().left || 16,
                          backgroundColor: 'color-mix(in srgb, var(--color-bg-secondary) 85%, transparent)',
                          backdropFilter: 'blur(20px) saturate(1.5)',
                          WebkitBackdropFilter: 'blur(20px) saturate(1.5)',
                          border: '1px solid var(--color-border-primary)',
                        }}
                        onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                      >
                        <button onClick={(e) => { e.stopPropagation(); guaImageInputRef.current?.click(); setShowAddMenu(false); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs generation-feedback hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-primary)' }}><ImageIcon size={14} style={{ color: theme.colors.accentPrimary }} /> 上传参考图</button>
                        <button onClick={(e) => { e.stopPropagation(); setShowGuaRefSelector(true); setShowAddMenu(false); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs generation-feedback hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-primary)' }}><FolderOpen size={14} style={{ color: theme.colors.accentPrimary }} /> 从资源库选择</button>
                        <button onClick={(e) => { e.stopPropagation(); guaVideoInputRef.current?.click(); setShowAddMenu(false); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs generation-feedback hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-primary)' }}><Video size={14} style={{ color: theme.colors.accentPrimary }} /> 上传参考视频</button>
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
              </>
            )}
            {/* 非 GuaVideo 参考图 */}
            <ModelArkReferenceLibraryPanel projectId={projectId} provider={selectedModel} references={generationMode === 'start-end' ? [images.start, images.end].filter(Boolean) : generationMode === 'text2video' ? [] : isDomesticOfficialSeedance(selectedModel) ? generationMode === 'img2video' ? [images.start].filter(Boolean) : [...getGuaImageItems(), ...guaRefVideos.map(url => ({ url, mediaType: 'Video' }))] : images.refs} />
            {!isGuaVideo && !isWanxVideo && !(isWandVegaVideo && (generationMode === 'img2video' || generationMode === 'start-end')) && images.refs.map((ref, i) => (
              <div key={ref.id} className="relative group w-14 h-14 rounded-md shrink-0"
                style={{ border: '1px solid var(--color-border-primary)' }}
                onMouseEnter={(e) => { const r = e.currentTarget.getBoundingClientRect(); setHoverPreview({ url: ref.url, x: r.left + r.width / 2, y: r.top }); }}
                onMouseLeave={() => setHoverPreview(null)}
              >
                {getSeedance25ReferenceMediaType(ref) === 'image' ? <img src={ref.url} className="w-full h-full object-cover rounded-md" alt="" /> : <span className="flex h-full items-center justify-center text-xs">{getSeedance25ReferenceMediaType(ref) === 'video' ? '视频' : '音频'}</span>}
                <div className="absolute bottom-0 left-0 right-0 text-center text-2xs font-bold py-px rounded-b-md" style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}>{({ image: '图片', video: '视频', audio: '音频' })[getSeedance25ReferenceMediaType(ref)]}{i + 1}</div>
                <button onClick={() => { setHoverPreview(null); removeImage('refs', ref.id); }} className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center generation-reference-remove generation-feedback" style={{ backgroundColor: theme.colors.error, color: 'white' }}><X size={8} /></button>
              </div>
            ))}
            {!isGuaVideo && !isWanxVideo && !(isWandVegaVideo && generationMode === 'img2video') && generationMode !== 'start-end' && (
              <div className="relative shrink-0" ref={!isGuaVideo && !isWanxVideo ? addMenuRef : undefined}>
                <button
                  onClick={() => setShowAddMenu(!showAddMenu)}
                  className="va-btn w-14 h-14 rounded-md flex flex-col items-center justify-center gap-1 shrink-0 generation-feedback"
                  style={{ border: `1px dashed ${mc.color || theme.colors.accentPrimary}50`, color: mc.color || theme.colors.accentPrimary }}
                >
                  <Plus size={16} />
                  <span className="text-2xs font-medium">添加</span>
                </button>
                {addMenuMount.mounted && !isGuaVideo && !isWanxVideo && ReactDOM.createPortal(
                  <div className="fixed inset-0 z-[300]" onMouseDown={() => setShowAddMenu(false)}>
                    <div
                      className={`absolute w-52 rounded-lg shadow-2xl overflow-hidden ${addMenuMount.exiting ? 'animate-out fade-out slide-out-to-bottom-2 duration-150' : 'animate-in fade-in slide-in-from-bottom-2 duration-150'}`}
                      style={{
                        bottom: window.innerHeight - (addMenuRef.current?.getBoundingClientRect().top || 0) + 8,
                        left: addMenuRef.current?.getBoundingClientRect().left || 16,
                        backgroundColor: 'color-mix(in srgb, var(--color-bg-secondary) 85%, transparent)',
                        backdropFilter: 'blur(20px) saturate(1.5)',
                        WebkitBackdropFilter: 'blur(20px) saturate(1.5)',
                        border: '1px solid var(--color-border-primary)',
                      }}
                      onClick={e => e.stopPropagation()}
                      onMouseDown={e => e.stopPropagation()}
                    >
                      <label onClick={(e) => e.stopPropagation()} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs cursor-pointer generation-feedback hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-primary)' }}>
                        <ImageIcon size={14} style={{ color: mc.color }} /> {supportsSeedance25MediaReferences(selectedModel) && generationMode === 'reference' ? '上传图片 / 视频 / 音频' : '上传图片'}
                        <input ref={imageInputRef} type="file" accept={supportsSeedance25MediaReferences(selectedModel) && generationMode === 'reference' ? 'image/*,video/*,audio/*' : 'image/*'} className="hidden" onChange={e => { handleImageUpload('refs', e.target.files[0], e.target); setShowAddMenu(false); }} />
                      </label>
                      <button onClick={(e) => { e.stopPropagation(); setShowResourceSelector(true); setShowAddMenu(false); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs generation-feedback hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-primary)' }}><FolderOpen size={14} style={{ color: mc.color }} /> 从资源库选择</button>
                    </div>
                  </div>,
                  document.body
                )}
              </div>
            )}
            {/* 首尾帧 */}
            {!isGuaVideo && !isWanxVideo && !isWandVegaVideo && generationMode === 'start-end' && (
              <>
                {images.start && (
                  <div className="relative group w-14 h-11 rounded-md shrink-0"
                    style={{ border: '1px solid var(--color-border-primary)' }}
                    onMouseEnter={(e) => { const r = e.currentTarget.getBoundingClientRect(); setHoverPreview({ url: images.start, x: r.left + r.width / 2, y: r.top }); }}
                    onMouseLeave={() => setHoverPreview(null)}
                  >
                    <img src={images.start} className="w-full h-full object-cover rounded-md" alt="" />
                    <button onClick={() => { setHoverPreview(null); removeImage('start'); }} className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center generation-reference-remove transition-opacity" style={{ backgroundColor: theme.colors.error, color: 'white' }}><X size={8} /></button>
                    <span className="absolute bottom-0 left-0 right-0 text-center text-2xs font-bold py-px rounded-b-md" style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}>首帧</span>
                  </div>
                )}
                {images.end && (
                  <div className="relative group w-14 h-11 rounded-md shrink-0"
                    style={{ border: '1px solid var(--color-border-primary)' }}
                    onMouseEnter={(e) => { const r = e.currentTarget.getBoundingClientRect(); setHoverPreview({ url: images.end, x: r.left + r.width / 2, y: r.top }); }}
                    onMouseLeave={() => setHoverPreview(null)}
                  >
                    <img src={images.end} className="w-full h-full object-cover rounded-md" alt="" />
                    <button onClick={() => { setHoverPreview(null); removeImage('end'); }} className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center generation-reference-remove transition-opacity" style={{ backgroundColor: theme.colors.error, color: 'white' }}><X size={8} /></button>
                    <span className="absolute bottom-0 left-0 right-0 text-center text-2xs font-bold py-px rounded-b-md" style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}>尾帧</span>
                  </div>
                )}
                <div className="relative shrink-0">
                  <label className="va-btn w-14 h-11 rounded-md flex flex-col items-center justify-center gap-0.5 shrink-0 generation-feedback cursor-pointer"
                    style={{ border: `1px dashed ${mc.color || theme.colors.accentPrimary}50`, color: mc.color || theme.colors.accentPrimary }}
                  >
                    <Plus size={14} />
                    <span className="text-2xs font-medium">{!images.start ? '首帧' : !images.end ? '尾帧' : '添加'}</span>
                    <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={e => { handleImageUpload(images.start ? 'end' : 'start', e.target.files[0], e.target); }} />
                  </label>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── 输入框卡片 ── */}
        {mode === 'generate' ? (
        <div className="generation-composer production-panel" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
          {/* 上半部分：输入框 */}
          <div className="flex items-start gap-2 p-3">
            {/* contentEditable 输入框 */}
            <div className="relative flex-1">
              <div
                ref={editorRef}
                contentEditable
                role="textbox"
                aria-label="视频提示词"
                aria-multiline="true"
                suppressContentEditableWarning
                onInput={handleEditorInput}
                onKeyDown={handleEditorKeyDown}
                onPaste={(e) => { const html = e.clipboardData.getData('text/html'); if (html && html.includes('data-mention')) return; e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); }}
                data-placeholder="描述你想要的视频画面...输入 @ 引用参考图"
                className="va-editor flex-1 bg-transparent text-sm outline-none leading-relaxed py-1 min-h-[128px] max-h-[280px] overflow-y-auto whitespace-pre-wrap break-words"
                style={{ color: 'var(--color-text-primary)' }}
              />
              {/* @ 提及弹窗 - 多级菜单 */}
              {mentionMenuMount.mounted && mentionPopover && ReactDOM.createPortal(
                <div className="fixed inset-0 z-[300]" onMouseDown={() => setMentionPopover(null)}>
                  <div
                    ref={mentionListRef}
                    className="studio-generation generation-popover fixed w-64 rounded-lg py-2 px-1 max-h-72 overflow-y-auto custom-scrollbar"
                    data-exiting={mentionMenuMount.exiting}
                    style={{
                      ...((() => {
                        const sel = window.getSelection();
                        let cursorRect = null;
                        if (sel?.rangeCount > 0) {
                          const range = sel.getRangeAt(0);
                          const rects = range.getClientRects();
                          cursorRect = rects.length > 0 ? rects[0] : range.getBoundingClientRect();
                        }
                        const fallback = editorRef.current?.getBoundingClientRect();
                        const bottom = cursorRect?.bottom || fallback?.bottom || 0;
                        const top = cursorRect?.top || fallback?.top || 0;
                        let left = cursorRect?.left || fallback?.left || 0;
                        const menuW = 260;
                        if (left + menuW > window.innerWidth - 8) left = window.innerWidth - menuW - 8;
                        if (left < 8) left = 8;
                        if (top < 300) {
                          return { top: bottom + 6, left };
                        }
                        return { bottom: window.innerHeight - top + 6, left };
                      })()),
                      backgroundColor: 'color-mix(in srgb, var(--color-bg-secondary) 92%, transparent)',
                      backdropFilter: 'blur(24px) saturate(1.6)',
                      WebkitBackdropFilter: 'blur(24px) saturate(1.6)',
                      border: '1px solid var(--color-border-primary)',
                    }}
                    onMouseDown={e => e.stopPropagation()}
                  >
                    {/* 面包屑导航 */}
                    {mentionPopover.path?.length > 0 && (
                      <div className="flex items-center gap-1 px-3 py-1.5 mb-1" style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
                        <button
                          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleMenuBack(); }}
                          className="text-2xs hover:underline"
                          style={{ color: 'var(--color-text-muted)' }}
                        >
                          ←
                        </button>
                        {mentionPopover.path.map((p, i) => (
                          <span key={i} className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                            {i > 0 && ' › '}{p}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* 加载中 */}
                    {menuLoading && (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 size={16} className="animate-spin" style={{ color: 'var(--color-text-muted)' }} />
                      </div>
                    )}

                    {/* 菜单项 */}
                    {!menuLoading && visibleMentions.map((item, i) => (
                      <button
                        key={item.id}
                        ref={el => mentionItemRefs.current[i] = el}
                        data-image-url={item.imageUrl || ''}
                        onMouseDown={(e) => {
                          e.preventDefault(); e.stopPropagation();
                          setMentionPreviewUrl(null);
                          if (item.action === 'select' || item.action === 'select-existing') {
                            handleMenuSelect(item);
                          } else {
                            handleMenuNavigate(item);
                          }
                        }}
                        onMouseEnter={() => updateMentionIndex(i)}
                        className="va-drop-item w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-left generation-feedback"
                        style={{
                          color: i === 0 && mentionIndexRef.current === 0 ? (mc.color || 'var(--color-accent-primary)') : 'var(--color-text-secondary)',
                          backgroundColor: i === 0 && mentionIndexRef.current === 0 ? `${mc.color || theme.colors.accentPrimary}18` : 'transparent',
                        }}
                      >
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" style={{ border: '1px solid var(--color-border-primary)' }} />
                        ) : item.icon ? (
                          <span className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ color: 'var(--color-text-secondary)' }}><item.icon size={16} /></span>
                        ) : null}
                        <span className="truncate flex-1">{item.label || item.name}</span>
                        {item.action === 'navigate' && (
                          <span style={{ color: 'var(--color-text-muted)' }}>›</span>
                        )}
                      </button>
                    ))}
                    {!menuLoading && hiddenMentionCount > 0 && (
                      <div className="px-3 py-2 text-center text-2xs" style={{ color: 'var(--color-text-muted)', borderTop: '1px solid var(--color-border-secondary)' }}>
                        还有 {hiddenMentionCount} 项，继续输入关键词缩小范围
                      </div>
                    )}

                    {!menuLoading && filteredMentions.length === 0 && (
                      <div className="px-3 py-4 text-center text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                        暂无内容
                      </div>
                    )}
                  </div>
                </div>,
                document.body
              )}

              {/* @ 本地上传隐藏 input */}
              <input type="file" ref={mentionUploadRef} className="hidden" accept={isWanxVideo ? WANX_VIDEO_ACCEPT.image : 'image/*'} onChange={handleMentionUpload} />
              <input type="file" ref={mentionVideoUploadRef} className="hidden" accept={isWanxVideo ? WANX_VIDEO_ACCEPT.video : 'video/mp4,video/quicktime,video/webm'} onChange={handleMentionVideoUpload} />
            </div>
          </div>

          {selectedModel === WAND_VEGA_PROVIDER && (
            <div role="note" className="mx-3 mb-2 rounded-md px-3 py-2 text-xs leading-relaxed" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border-primary)' }}>
              <div className="flex items-start gap-2">
                <AlertCircle size={14} className="mt-0.5 shrink-0" style={{ color: '#f59e0b' }} />
                <div>
                  <p><strong>测试期间使用站内暂定价格。</strong> 已开放 720p、1080p、2K 和 4K，预计积分如下，具体素材组合和账号权限以腾讯服务端为准。</p>
                  <EstimatedPoints request={vegaCostRequest} compact className="mt-1" />
                </div>
              </div>
            </div>
          )}

          {/* 下半部分：模型tab + 参数 + 发送按钮 */}
          <div className="generation-controls flex items-center justify-between px-3 pb-2.5 pt-0">
            <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
              {/* 模型切换 pill */}
              {visibleModelConfigs.map(mc => {
                const isSel = selectedModel === mc.id;
                const modelColor = mc.color || theme.colors.accentPrimary;
                return (
                  <button key={mc.id} onClick={() => { if (mc.id !== selectedModel) { draftContextRef.current = { ...draftContextRef.current }; } setSelectedModel(mc.id); setMode('generate'); }}
                    className="va-btn generation-choice flex items-center gap-1.5 px-2.5 py-1 rounded-md text-2xs font-semibold whitespace-nowrap"
                    aria-pressed={isSel && mode === 'generate'}
                    style={{
                      backgroundColor: isSel && mode === 'generate' ? `${modelColor}22` : 'transparent',
                      color: isSel && mode === 'generate' ? modelColor : 'var(--color-text-muted)',
                      border: `1px solid ${isSel && mode === 'generate' ? `${modelColor}55` : 'transparent'}`,
                    }}
                  ><span className="va-dot" style={{ backgroundColor: modelColor }} />{mc.name}</button>
                );
              })}

              <div className="w-px h-4 mx-0.5" style={{ backgroundColor: 'var(--color-border-primary)' }} />

              {/* 后处理 pills */}
              <button onClick={() => setMode(mode === 'removeSubtitle' ? 'generate' : 'removeSubtitle')}
                className="va-btn generation-choice flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-semibold whitespace-nowrap"
                aria-pressed={mode === 'removeSubtitle'}
                style={{
                  backgroundColor: mode === 'removeSubtitle' ? 'var(--color-red-light)' : 'transparent',
                  color: mode === 'removeSubtitle' ? 'var(--color-error)' : 'var(--color-text-muted)',
                  border: `1px solid ${mode === 'removeSubtitle' ? 'var(--color-border-primary)' : 'transparent'}`,
                }}
              >去字幕</button>

              {mode === 'generate' && (
                <>
                  <div className="w-px h-4 mx-0.5" style={{ backgroundColor: 'var(--color-border-primary)' }} />

                  {/* 生成模式 */}
                  {mc.modes.map(mode => {
                    const isSel = generationMode === mode;
                    const labels = { text2video: '文生', img2video: '图生', 'start-end': '首尾帧', reference: isWanxVideo ? '全能参考' : '参考图', ref2v: '参考生视频', orbit: '环绕', 'subject-reference': '主体参考', 'edit-video': '编辑视频', 'extend-video': '延长视频' };
                    return (
                      <button key={mode} onClick={() => handleGenerationModeChange(mode)}
                        className="va-btn generation-choice px-2 py-1 rounded-md text-2xs font-medium whitespace-nowrap"
                        aria-pressed={isSel}
                    style={{
                      backgroundColor: isSel ? `${mc.color}18` : 'transparent',
                      color: isSel ? mc.color : 'var(--color-text-muted)',
                      border: `1px solid ${isSel ? `${mc.color}45` : 'transparent'}`,
                    }}
                  >{labels[mode] || mode}</button>
                );
              })}

              <div className="w-px h-4 mx-0.5" style={{ backgroundColor: 'var(--color-border-primary)' }} />

              {/* 子模型 — pill 按钮组 */}
              {mc.models.map(m => {
                const isSel = config.model === m.value;
                return (
                  <button key={m.value} onClick={() => {
                    if (selectedModel !== WAND_VEGA_PROVIDER) { updateConfig('model', m.value); return; }
                    setConfig((current) => ({
                      ...current,
                      model: m.value,
                      duration: getWandVegaDurations(m.value).includes(current.duration) ? current.duration : '15s',
                    }));
                  }}
                    className="va-btn generation-choice px-2 py-1 rounded-md text-2xs font-medium whitespace-nowrap"
                    aria-pressed={isSel}
                    title={m.desc || m.label}
                    style={{
                      backgroundColor: isSel ? `${mc.color}25` : 'transparent',
                      color: isSel ? mc.color : 'var(--color-text-muted)',
                    }}
                  >{m.label}</button>
                );
              })}

              <div className="w-px h-4 mx-0.5" style={{ backgroundColor: 'var(--color-border-primary)' }} />

              {/* 时长 */}
              <DropdownPicker
                value={config.duration}
                ariaLabel="视频时长"
                options={mc.durations}
                color={mc.color}
                onChange={(v) => updateConfig('duration', v)}
              />

              {/* 比例 — 毛玻璃弹窗选择 */}
              {/* 比例 */}
              <DropdownPicker
                label={activeVideoRatio === 'adaptive' ? '自适应' : activeVideoRatio}
                ariaLabel="视频比例"
                value={activeVideoRatio}
                options={activeVideoRatios}
                onChange={handleVideoRatioChange}
              />

              {/* 分辨率 — 毛玻璃弹窗选择（仅 GuaVideo） */}
              {/* 分辨率 */}
              {mc.resolutions && (
                <DropdownPicker
                  value={config.resolution || mc.defaults?.resolution || '720p'}
                  ariaLabel="视频分辨率"
                  options={mc.resolutions}
                  onChange={(v) => updateConfig('resolution', v)}
                />
              )}

              {/* GuaVideo 有声 — toggle pill */}
              {(isGuaVideo || selectedModel === WAND_VEGA_PROVIDER || getSeedance25Capabilities(selectedModel)?.supportsGenerateAudioControl) && (
                <button onClick={() => updateConfig('generateAudio', !(config.generateAudio ?? true))}
                  className="va-btn generation-choice px-2 py-1 rounded-md text-2xs font-medium whitespace-nowrap"
                  aria-pressed={config.generateAudio ?? true}
                  style={{
                    backgroundColor: (config.generateAudio ?? true) ? `${mc.color}25` : 'var(--color-bg-tertiary)',
                    color: (config.generateAudio ?? true) ? mc.color : 'var(--color-text-muted)',
                  }}
                >{(config.generateAudio ?? true) ? '有声' : '静音'}</button>
              )}
              {isGuaVideo && (
                <button onClick={() => updateConfig('noSubtitle', !config.noSubtitle)}
                  className="va-btn generation-choice px-2 py-1 rounded-md text-2xs font-medium whitespace-nowrap"
                  aria-pressed={Boolean(config.noSubtitle)}
                  style={{
                    backgroundColor: config.noSubtitle ? `${mc.color}25` : 'var(--color-bg-tertiary)',
                    color: config.noSubtitle ? mc.color : 'var(--color-text-muted)',
                  }}
                >{config.noSubtitle ? '去字幕' : '字幕'}</button>
              )}

              {/* 打光选择器 — 按钮+下拉列表 */}
              {/* 打光选择器 */}
              <DropdownPicker
                label={selectedLighting ? (LIGHTING_PRESETS.find(lp => lp.id === selectedLighting)?.name || '打光') : '打光'}
                ariaLabel="视频打光"
                value={selectedLighting || ''}
                options={[{ value: '', label: '无打光' }, ...LIGHTING_PRESETS.map(lp => ({ value: lp.id, label: `${lp.emoji} ${lp.name}` }))]}
                onChange={(v) => setSelectedLighting(v || null)}
              />

              {/* Vidu 音频 — toggle pill */}
              {selectedModel === 'vidu' && (
                <button onClick={() => updateConfig('audio', !(config.audio ?? true))}
                  className="va-btn generation-choice px-2 py-1 rounded-md text-2xs font-medium whitespace-nowrap"
                  aria-pressed={config.audio ?? true}
                  style={{
                    backgroundColor: (config.audio ?? true) ? `${mc.color}25` : 'var(--color-bg-tertiary)',
                    color: (config.audio ?? true) ? mc.color : 'var(--color-text-muted)',
                  }}
                >{(config.audio ?? true) ? '音频' : '静音'}</button>
              )}

              {activeSeedance25Capabilities?.supportsWatermarkControl && (
                <button onClick={() => updateConfig('watermark', !config.watermark)}
                  className="va-btn generation-choice px-2 py-1 rounded-md text-2xs font-medium whitespace-nowrap"
                  aria-pressed={Boolean(config.watermark)}
                  style={{
                    backgroundColor: config.watermark ? `${mc.color}25` : 'var(--color-bg-tertiary)',
                    color: config.watermark ? mc.color : 'var(--color-text-muted)',
                  }}
                  title="在生成视频中添加水印"
                >{config.watermark ? '有水印' : '无水印'}</button>
              )}

              {/* 万相 3.0 生成选项 */}
              {isWanxVideo && (
                <>
                  <button onClick={() => updateConfig('audio', !(config.audio ?? true))}
                    className="va-btn generation-choice px-2 py-1 rounded-md text-2xs font-medium whitespace-nowrap"
                    aria-pressed={config.audio ?? true}
                    style={{
                      backgroundColor: (config.audio ?? true) ? `${mc.color}25` : 'var(--color-bg-tertiary)',
                      color: (config.audio ?? true) ? mc.color : 'var(--color-text-muted)',
                    }}
                    title="生成有声视频"
                  >{(config.audio ?? true) ? '有声' : '静音'}</button>
                  <button onClick={() => updateConfig('watermark', !config.watermark)}
                    className="va-btn generation-choice px-2 py-1 rounded-md text-2xs font-medium whitespace-nowrap"
                    aria-pressed={Boolean(config.watermark)}
                    style={{
                      backgroundColor: config.watermark ? `${mc.color}25` : 'var(--color-bg-tertiary)',
                      color: config.watermark ? mc.color : 'var(--color-text-muted)',
                    }}
                    title="在生成视频中添加水印"
                  >{config.watermark ? '有水印' : '无水印'}</button>
                  <label
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-2xs whitespace-nowrap"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}
                    title="留空表示随机，可填 0-2147483647"
                  >
                    <span>Seed</span>
                    <input
                      type="number"
                      min="0"
                      max="2147483647"
                      step="1"
                      value={config.seed ?? ''}
                      onChange={(event) => updateConfig('seed', event.target.value)}
                      placeholder="随机"
                      className="w-[74px] bg-transparent outline-none text-2xs"
                      style={{ color: 'var(--color-text-secondary)' }}
                    />
                  </label>
                  <span
                    className="text-2xs whitespace-nowrap"
                    style={{ color: 'var(--color-text-muted)' }}
                    title="任务成功后系统会立即转存视频"
                  >成功即转存 · 链接24h</span>
                </>
              )}
                </>
              )}
            </div>

            {/* 发送按钮 — 右下角（仅生成模式显示） */}
            {mode === 'generate' && (
              <button
                onClick={handleGenerate}
                disabled={isSubmitting || (isWanxVideo && wanxUploading)}
                className="va-btn generation-primary-action w-9 h-9 rounded-md flex items-center justify-center disabled:opacity-40 shrink-0 ml-2"
                style={{ backgroundColor: mc.color || theme.colors.accentPrimary, color: 'white' }}
                aria-label="生成视频"
                title="生成视频"
              >
                {(isSubmitting || (isWanxVideo && wanxUploading)) ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            )}
          </div>
        </div>
        ) : (
          /* 后处理输入区 */
          <PostProcessInput
            sourceVideo={ppSourceVideo}
            onSourceVideoChange={setPpSourceVideo}
            onSubmit={handlePostProcess}
            isSubmitting={isSubmitting}
            onOpenResourceSelector={() => setShowPpResourceSelector(true)}
            onLocalUpload={handlePpVideoUpload}
            theme={theme}
          />
        )}
      </div>

            {/* 隐藏的文件 input */}
      <input ref={guaImageInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={async (e) => {
        const file = e.target.files?.[0]; if (!file) return;
        const uploadContext = draftContextRef.current;
        if (file.size > 30 * 1024 * 1024) { toast?.warning('图片不能超过30MB'); return; }
        try {
          const url = await uploadFileToCos(file);
          if (uploadContext !== draftContextRef.current) return;
          const name = file.name.split('.')[0] || '本地图片';
          setGuaRefImages(prev => prev.includes(url) ? prev : [...prev, url]);
          appendChipToEditor(name, url, 'upload');
          setGuaRefNames(prev => new Map(prev).set(url, name));
        } catch (err) { toast?.error('上传失败'); }
        finally { e.target.value = ''; }
      }} />
      <input ref={guaVideoInputRef} type="file" accept="video/mp4,video/quicktime" className="hidden" onChange={async (e) => {
        const file = e.target.files?.[0]; if (!file) return;
        const uploadContext = draftContextRef.current;
        if (file.size > 50 * 1024 * 1024) { toast?.warning('视频不能超过50MB'); return; }
        try {
          const url = await uploadFileToCos(file);
          if (uploadContext !== draftContextRef.current) return;
          const name = file.name.split('.')[0] || `视频${guaRefVideos.length + 1}`;
          setGuaRefVideos(prev => prev.includes(url) ? prev : [...prev, url]);
          setGuaRefNames(prev => new Map(prev).set(url, name));
        } catch (err) { toast?.error('上传失败'); }
        finally { e.target.value = ''; }
      }} />
      <input ref={wanxImageInputRef} type="file" accept={WANX_VIDEO_ACCEPT.image} className="hidden" onChange={event => handleWanxMediaUpload(event, WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE)} />
      <input ref={wanxVideoInputRef} type="file" accept={WANX_VIDEO_ACCEPT.video} className="hidden" onChange={event => handleWanxMediaUpload(event, WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO)} />
      <input ref={wanxAudioInputRef} type="file" accept={WANX_VIDEO_ACCEPT.audio} className="hidden" onChange={event => handleWanxMediaUpload(event, WANX_VIDEO_MEDIA_TYPES.REFERENCE_AUDIO)} />
      <input ref={wanxFileInputRef} type="file" accept={WANX_VIDEO_ACCEPT.file} className="hidden" onChange={event => handleWanxMediaUpload(event, WANX_VIDEO_MEDIA_TYPES.FILE)} />
      {/* 资源选择器（始终挂载，由组件内部播进出场动画） */}
      <ResourceSelector isOpen={showResourceSelector} onClose={() => setShowResourceSelector(false)}
        onSelect={(res) => {
          const url = isDreaminaProxySeedance20Provider(selectedModel) ? getDreaminaProxyOriginalUrl(res)
            : getSeedance25Capabilities(selectedModel) ? getSeedance25ReferenceUrl(res) : res.url || res.thumbnailUrl;
          if (!url && isDreaminaProxySeedance20Provider(selectedModel)) { toast?.warning('旧素材 ID 没有原始 URL，请重新选择素材'); return; }
          if (!url) return;
          if (selectedModel === WAND_VEGA_PROVIDER) {
            const descriptor = `${res.category || ''} ${res.typeCode || ''} ${res.type || ''} ${res.resourceType || ''}`.toLowerCase();
            const cleanUrl = String(url).split('?')[0].split('#')[0];
            if (descriptor.includes('video') || descriptor.includes('audio') || /\.(mp4|mov|webm|m4v|mp3|wav|m4a|aac|ogg|flac)$/i.test(cleanUrl)) {
              toast?.warning?.('Wand Vega 当前入口仅支持参考图片，请选择图片资源');
              return;
            }
            if (!isPublicWandMediaUrl(url)) {
              toast?.warning?.('Wand Vega 需要公网可访问的原始图片 URL，不能使用旧素材库 ID');
              return;
            }
            if (generationMode === 'img2video' || generationMode === 'start-end') {
              setImages((current) => ({ ...current, [getWandFrameTarget(generationMode, current)]: url }));
              return;
            }
          }
          if (isSeedance25 && !(supportsSeedance25MediaReferences(selectedModel) && generationMode === 'reference')) {
            const descriptor = `${res.category || ''} ${res.typeCode || ''} ${res.type || ''} ${res.resourceType || ''}`.toLowerCase();
            const cleanUrl = String(url).split('?')[0].split('#')[0];
            if (descriptor.includes('video') || descriptor.includes('audio') || /\.(mp4|mov|webm|m4v|mp3|wav|m4a|aac|ogg|flac)$/i.test(cleanUrl)) {
              toast?.warning?.(`${activeSeedance25Capabilities?.label || '即梦 2.5'} 这个入口只接收参考图，请选择图片资源`);
              return;
            }
          }
          if (isWanxVideo && generationMode === 'reference') {
            const mediaType = inferWanxVideoReferenceType(res, url);
            const item = createWanxVideoMediaItem({
              type: mediaType,
              url,
              name: res.name || res.prompt || getMediaNameFromUrl(url, '参考素材'),
              duration: res.duration,
            });
            if (addWanxMediaItem(item)) {
              appendChipToEditor(item.name || getWanxVideoMediaLabel(item, [...wanxMedia, item]), url, mediaType);
            }
            return;
          }
          if (activeSeedance25Capabilities && images.refs.length >= activeSeedance25Capabilities.maxReferenceImages) {
            toast?.warning?.(`${activeSeedance25Capabilities.label} 最多支持 ${activeSeedance25Capabilities.maxReferenceImages} 张参考图`);
            return;
          }
          setImages(prev => ({
            ...prev,
            refs: [...prev.refs, {
              id: Date.now(),
              url,
              name: res.name || res.prompt || `参考图${prev.refs.length + 1}`,
              type: res.category || res.typeCode || res.type || '参考图',
              mediaType: getSeedance25ReferenceMediaType({ ...res, type: res.category || res.typeCode || res.type, url }),
            }],
          }));
        }}
        projectId={projectId} type="all" title="选择资源"
      />
      <ResourceSelector isOpen={showGuaRefSelector} onClose={() => setShowGuaRefSelector(false)}
        onSelect={(res) => {
          const url = isDreaminaProxySeedance20Provider(selectedModel) ? getDreaminaProxyOriginalUrl(res)
            : getSeedance25Capabilities(selectedModel) ? getSeedance25ReferenceUrl(res) : res.url || res.thumbnailUrl;
          if (!url && isDreaminaProxySeedance20Provider(selectedModel)) { toast?.warning('旧素材 ID 没有原始 URL，请重新选择素材'); return; }
          if (!url) return;
          const typeText = `${res.category || ''} ${res.typeCode || ''} ${res.type || ''} ${res.resourceType || ''}`.toLowerCase();
          const cleanUrl = String(url).split('?')[0].split('#')[0];
          const isVideo = typeText.includes('video') || /\.(mp4|mov|webm|m4v)$/i.test(cleanUrl);
          const isAudio = typeText.includes('audio') || typeText.includes('voice') || /\.(mp3|wav|m4a|aac|ogg|flac)$/i.test(cleanUrl);
          if (isVideo) {
            setGuaRefVideos(prev => [...prev, url]);
            setGuaRefNames(prev => new Map(prev).set(url, res.name || getMediaNameFromUrl(url, `视频${guaRefVideos.length + 1}`)));
          } else if (isAudio) {
            toast?.warning(`${mc.name} 已停用参考音频，请选择图片或视频`);
          } else {
            setGuaRefImages(prev => { const n = [...prev, url]; appendChipToEditor(res.name || `图片${n.length}`, url); return n; });
            setGuaRefNames(prev => new Map(prev).set(url, res.name || `图片${guaRefImages.length + 1}`));
            rememberGuaRefMeta(url, res, res.source || res.sourceType || res.typeCode || 'image');
          }
        }}
        projectId={projectId} type="all" title="选择参考素材"
      />
      <ResourceSelector isOpen={showFrameResourceSelector} onClose={() => setShowFrameResourceSelector(false)}
        onSelect={(res) => {
          const url = isDreaminaProxySeedance20Provider(selectedModel) ? getDreaminaProxyOriginalUrl(res)
            : getSeedance25Capabilities(selectedModel) ? getSeedance25ReferenceUrl(res) : res.url || res.thumbnailUrl;
          if (!url && isDreaminaProxySeedance20Provider(selectedModel)) { toast?.warning('旧素材 ID 没有原始 URL，请重新选择素材'); return; }
          if (!url) return;
          if (isWandVegaVideo) {
            const descriptor = `${res.category || ''} ${res.typeCode || ''} ${res.type || ''} ${res.resourceType || ''}`.toLowerCase();
            const cleanUrl = String(url).split('?')[0].split('#')[0];
            if (descriptor.includes('video') || descriptor.includes('audio') || /\.(mp4|mov|webm|m4v|mp3|wav|m4a|aac|ogg|flac)$/i.test(cleanUrl)) {
              toast?.warning?.('Wand Vega 的首帧和尾帧只能选择图片资源');
              return;
            }
            if (!isPublicWandMediaUrl(url)) {
              toast?.warning?.('Wand Vega 需要公网可访问的原始图片 URL，不能使用旧素材库 ID');
              return;
            }
          }
          if (isWanxVideo) {
            if (inferWanxVideoReferenceType(res, url) !== WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE) {
              toast?.warning('首帧和尾帧只能选择图片素材');
              return;
            }
            const mediaType = frameTarget === 'end'
              ? WANX_VIDEO_MEDIA_TYPES.LAST_FRAME
              : WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME;
            addWanxMediaItem(createWanxVideoMediaItem({
              type: mediaType,
              url,
              name: res.name || res.prompt || getMediaNameFromUrl(url, getWanxVideoMediaLabel({ type: mediaType }, [])),
            }));
            setShowFrameResourceSelector(false);
            return;
          }
          setImages(prev => ({ ...prev, [frameTarget]: url }));
          setShowFrameResourceSelector(false);
        }}
        projectId={projectId} type="all" title={frameTarget === 'start' ? '选择首帧图片' : '选择尾帧图片'}
      />
      <ResourceSelector isOpen={showPpResourceSelector} onClose={() => setShowPpResourceSelector(false)}
        onSelect={(res) => {
          const url = getSeedance25Capabilities(selectedModel) ? getSeedance25ReferenceUrl(res) : res.url || res.thumbnailUrl;
          if (!url) return;
          setPpSourceVideo({
            id: res.id,
            resourceId: res.resourceId ?? res.id,
            url,
            name: res.name || res.prompt || '视频',
            provider: res.source,
            duration: res.duration,
          });
          setShowPpResourceSelector(false);
        }}
        projectId={projectId} type="video" title="选择要处理的视频"
      />

      {/* @ 菜单选中项大图预览（独立组件，不触发主组件重渲染） */}
      <MentionPreview url={mentionPreviewUrl} anchorRef={mentionListRef} />

      {/* 参考图缩略图 hover 大图预览（带左右自适应） */}
      {hoverPreviewMount.mounted && activeHoverPreview && ReactDOM.createPortal(
        <div className="fixed z-[400] pointer-events-none" style={{
          ...((() => {
            const previewWidth = Math.min(400, window.innerWidth * 0.3) + 20;
            const spaceLeft = activeHoverPreview.x - previewWidth / 2;
            if (spaceLeft < 0) {
              return { left: activeHoverPreview.x + 40, right: 'auto' };
            }
            return { left: Math.max(8, activeHoverPreview.x - previewWidth / 2), right: 'auto' };
          })()),
          bottom: window.innerHeight - activeHoverPreview.y + 8,
        }}>
          <img src={activeHoverPreview.url} alt="" className={`rounded-xl shadow-2xl ${hoverPreviewMount.exiting ? 'animate-out fade-out zoom-out-95 duration-150' : 'animate-in fade-in zoom-in-95 duration-150'}`} style={{
            maxWidth: 'min(400px, 30vw)', maxHeight: 'min(400px, 40vh)', width: 'auto', height: 'auto', objectFit: 'contain',
            backgroundColor: 'color-mix(in srgb, var(--color-bg-overlay) 95%, transparent)',
            border: '1px solid var(--color-border-primary)',
            padding: '4px',
          }} />
        </div>,
        document.body
      )}

      {/* 删除确认 */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDeleteVideo}
        title={deleteTarget?.status === 'success' ? '移入回收站' : '移除本地记录'}
        message={deleteTarget?.status === 'success' ? '确定将这条视频记录移入回收站吗？移入后将不再显示，可联系管理员恢复。' : '仅移除本地任务卡片，不会取消已提交的生成任务。'}
        confirmLabel={deleteTarget?.status === 'success' ? '移入回收站' : '移除记录'}
        variant="danger"
      />
    </div>
  );
};

export default VideoAgentModern;
